<template>
  <!-- A dock, not a takeover. `role="region"` and no `aria-modal`, because
       nothing behind this is inert any more: the puck grid stays visible,
       scrollable and tappable, and telling a screen reader otherwise would be
       the one lie the whole redesign exists to stop telling. -->
  <div
    class="sa-voice"
    :class="dockClass"
    :style="dockStyle"
    role="region"
    aria-label="Voice"
  >
    <!-- Sheet only. A 44 px band holding a 36 px pill: the pill is the
         affordance, the band is the target. It answers a drag between the two
         snaps AND a tap, because a tap is what a gloved hand does to a handle
         and a handle that ignores one reads as broken. -->
    <div
      v-if="isSheet"
      class="sa-voice-grip"
      role="button"
      tabindex="0"
      :aria-label="gripLabel"
      :aria-expanded="sheetSnap == 'expanded' ? 'true' : 'false'"
      @pointerdown="onGripDown"
      @pointermove="onGripMove"
      @pointerup="onGripUp"
      @pointercancel="onGripUp"
      @keydown="onGripKeydown"
    >
      <span class="sa-voice-grip-bar" aria-hidden="true"></span>
    </div>

    <!-- Header. Deliberately thin: the working area is the preview and the
         thumb zone, not the chrome. The state word, its countdown ring and the
         exit sentence are one component now; the gear holds every preference
         that used to be scattered through the card and the footer. -->
    <header class="sa-voice-head">
      <voice-state-line
        :word="stateWord"
        :deadline="deadline"
        :exit-copy="exitCopy"
        :ui-state="uiState"
      />

      <voice-settings-menu
        :prefs="prefs"
        :mic-line="micSettingsText"
        @update:prefs="applyPrefs"
      />

      <button
        type="button"
        class="sa-voice-close"
        aria-label="Close voice control"
        @click="requestClose"
      >
        &times;
      </button>
    </header>

    <main class="sa-voice-body">
      <!-- Where the puck identity came from — open on screen, tapped, scanned,
           glanced at — and the button that will change it once the phone app
           can scan. The "this"/"here" hint lives here too. -->
      <voice-context-chip
        :context="$store.state.voiceScreenContext"
        :scan-available="false"
        @scan="onScan"
      />

      <!-- The two pucks that matter, each under its Dewar › Rack › Puck line.
           Replaces the animated stage: the grid behind the dock is the truth
           now, and "Show in rack" takes the user to it. -->
      <voice-focus-view
        v-if="focusModel"
        :model="focusModel"
        @show-in-rack="showInRack"
      />

      <!-- The conversation, in the order it happened: what the microphone
           heard, then what the model said back. Both stay on screen while the
           user answers, because the answer is nearly always a correction to one
           of them. -->
      <div v-if="heardText || assistantReply" class="sa-voice-talk">
        <!-- What the microphone heard, one tappable word at a time. A mishear
             is a two-tap correction: fix the word, and the corrected sentence
             is parsed again. The live partial is never editable. -->
        <voice-transcript
          :text="transcript"
          :partial="partialTranscript"
          :editable="canEditTranscript"
          @reparse="onReparse"
        />

        <!-- The model's own words. It answers in speech whenever it cannot fill
             a command in — "which puck?" — and that reply used to go nowhere at
             all. -->
        <p v-if="assistantReply" class="sa-voice-reply">
          <span class="sa-voice-reply-label">Assistant</span>
          <span class="sa-voice-reply-text">{{ assistantReply }}</span>
        </p>

        <!-- Only while the microphone is actually open: telling someone to
             answer a question the session can no longer hear would be a lie. -->
        <p v-if="assistantReply && !preview && micOpen" class="sa-voice-reply-next">
          Answer it, or voice stops shortly.
        </p>
      </div>

      <!-- Preview. An `idle` surface styled as a form, not an alert
           (DESIGN.md 9). Nothing here has happened yet. -->
      <section v-if="preview" class="sa-voice-card" :class="{ 'sa-voice-card-destructive': isDestructive }">
        <h2 class="sa-voice-card-title">{{ intentTitle }}</h2>

        <!-- The destructive banner leads, before the detail rows, because a
             user scanning in gloves reads the first line and the buttons. -->
        <p v-if="isDestructive" class="sa-voice-destructive-banner">
          This permanently deletes the box.
          <template v-if="gridsLost > 0">
            {{ gridsLost }} non-empty grid{{ gridsLost == 1 ? "" : "s" }} will be
            lost.
          </template>
          <template v-else>It holds no non-empty grids.</template>
        </p>

        <dl class="sa-voice-rows">
          <template v-for="row in previewRows" :key="row.label">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </template>
        </dl>

        <!-- Where the user stands, in one line from `cardStatusLine`. Three
             cases: the session ended under a card they are still reading (say
             so here, not only in the state word — this is the sentence that
             answers "did my delete go through", and the footer has already
             replaced Confirm with Resume voice to match); the paid stream was
             torn down for being idle while the card and the microphone stayed
             (Confirm still works, a spoken "confirm" needs a tap first); or
             nothing has happened yet, as before. -->
        <p
          v-if="cardStatus.kind == 'stopped'"
          class="sa-voice-not-applied"
        >
          {{ cardStatus.text }}
        </p>
        <p v-else class="sa-voice-nothing-yet">{{ cardStatus.text }}</p>
      </section>

      <!-- Refusal. `warning`: understood and refused is the system working. -->
      <section v-else-if="refusal" class="sa-voice-refusal">
        <p class="sa-voice-refusal-title">{{ refusal.title }}</p>
        <p class="sa-voice-refusal-detail">{{ refusal.detail }}</p>
        <p class="sa-voice-refusal-next">{{ refusal.next }}</p>
        <!-- "Which one?" — the resolver's candidates as chips. Tap one, say
             the puck, or (Stage 2) look at it. -->
        <voice-candidates
          v-if="candidates"
          :candidates="candidates"
          @choose="onChoose"
        />
      </section>

      <!-- Applied receipt. -->
      <section v-else-if="applied" class="sa-voice-applied">
        <p class="sa-voice-applied-title">{{ applied.title }}</p>
        <p class="sa-voice-applied-detail">{{ applied.detail }}</p>
      </section>

      <!-- Resting hint. Examples rather than instructions: a user in gloves
           copies a phrase, they do not read a grammar. Withdrawn once the model
           has asked a question — the reply above it is the instruction now, and
           three unrelated examples under it would compete with it. -->
      <div v-else-if="!assistantReply" class="sa-voice-hint">
        <p class="sa-voice-hint-lead">Say what you want to do.</p>
        <ul class="sa-voice-hint-list">
          <li>&ldquo;Move box {{ hintBox }} to {{ hintPuck }} well seven.&rdquo;</li>
          <li>&ldquo;Create a box in {{ hintPuck }} well four.&rdquo;</li>
          <li>&ldquo;Delete box {{ hintBox }}.&rdquo;</li>
        </ul>
        <p class="sa-voice-hint-foot">
          Every command is shown here for you to confirm before anything changes.
        </p>
      </div>
    </main>

    <!-- Thumb zone. Everything tappable lives down here; the header close is a
         duplicate for reachability on a desk, not the primary way out. -->
    <footer class="sa-voice-foot">
      <!-- Signal-driven input meter. See DESIGN.md section 10: this moves
           because the microphone is moving it, and flatlines the moment audio
           stops, which is the property a fixed-rate animation cannot have. -->
      <div
        class="sa-voice-meter"
        :class="{ 'sa-voice-meter-live': meterLive }"
        role="img"
        :aria-label="meterLabel"
      >
        <span
          v-for="bar in meterBars"
          :key="bar"
          class="sa-voice-meter-bar"
          :class="{ 'sa-voice-meter-bar-on': bar <= meterLevel }"
        />
      </div>
      <p class="sa-voice-meter-caption">{{ meterLabel }}</p>
      <!-- The estimate is one number so the footer stays one line; the title
           is where the three things that make it up are named, because "why is
           it more than the minutes" is the only question this line raises. -->
      <p class="sa-voice-usage" :title="usageTitle">{{ usageText }}</p>

      <!-- The three numbers that decide whether this feels instant, and which
           were invisible until now. An em dash means "not measured on this
           turn", never zero: a latency of nought seconds is a claim, and a
           missing measurement is not one. -->
      <p v-if="showLatency" class="sa-voice-latency">{{ latencyText }}</p>

      <!-- The microphone control. Hold to talk gates ONE hands-free session
           (zeros flow while the button is up, see voiceHoldGate.js); in
           hands-free mode the same button mutes and unmutes. -->
      <voice-hold-button
        :mode="prefs.micMode"
        :live="sessionLive"
        :holding="holding"
        @hold-start="onHoldStart"
        @hold-end="onHoldEnd"
        @toggle-free="onToggleFree"
        @toggle-hold="onToggleHold"
      />

      <!-- ONE pair, from `footerButtons`. The four near-identical templates
           this replaces each decided for themselves whether a card could still
           be confirmed; now the table in `voiceDockLayout.js` decides once, and
           a node test reads it. Only the PAINTED order moves with
           `flex-direction` — DOM and tab order stay "act, then leave". -->
      <div class="sa-voice-actions" :style="{ flexDirection: actionDirection }">
        <button
          v-for="button in footerActions"
          :key="button.action"
          type="button"
          class="sa-voice-btn"
          :class="'sa-voice-btn-' + button.tone"
          :disabled="isActionDisabled(button)"
          @click="runAction(button.action)"
        >
          {{ button.label }}
        </button>
      </div>
    </footer>
  </div>
</template>

<script>
import VoiceSession from "../../utils/voiceSession.js";
import VoiceStateLine from "./VoiceStateLine.vue";
import VoiceSettingsMenu from "./VoiceSettingsMenu.vue";
import VoiceFocusView from "./VoiceFocusView.vue";
import VoiceContextChip from "./VoiceContextChip.vue";
import VoiceTranscript from "./VoiceTranscript.vue";
import VoiceCandidates from "./VoiceCandidates.vue";
import VoiceHoldButton from "./VoiceHoldButton.vue";
import { makeFocusModel } from "../../utils/voiceFocusModel.js";
import { locatePuck } from "../../utils/puckLocator.js";
import { reparseRequest } from "../../utils/voiceTranscriptEdit.js";
import {
  candidatesFromRefusal,
  fieldsWithChoice,
} from "../../utils/voiceCandidates.js";
import {
  reparseOutcome,
  turnLink,
  listeningPhase,
  isSessionLive,
  voiceConfirmAllowed,
  confirmationMatchesCard,
  confirmModeOf,
  isTypingTarget,
  isStaleGeneration,
  speechRoute,
  micMeterCaption,
  micSettingsLine,
  costBreakdown,
  billedVsSpoken,
} from "../../utils/voiceDockWiring.js";
import { defaultMicMode, readPrefs, writePrefs } from "../../utils/voiceDockPrefs.js";
import { confirmWindowMs } from "../../utils/voiceTimers.js";
import { createTimingLedger } from "../../utils/voiceTiming.js";
// The relay socket, for the one message that has no HTTP body to ride on.
//
// The dock does not otherwise speak to the socket — `VoiceSession` owns the
// transport and everything else goes through it. This is here because the last
// timing snapshot of a turn is only complete AFTER the apply has resolved and
// the receipt has drawn, by which point there is no request left to attach it
// to, and because a session may legitimately have ended by then: the ledger
// belongs to the dock, so the dock posts it.
import socket from "../../socket";
import {
  PANEL_WIDTH_PX,
  SHEET_SNAPS,
  layoutFor,
  nextSnap,
  sheetHeightPx,
  snapForFraction,
  exitCopyFor,
  footerButtons,
  footerDirection,
  cardStatusLine,
} from "../../utils/voiceDockLayout.js";

// Fallback destructive list. The server's /commands registry is authoritative,
// but if that fetch fails this must never make a confirmation LIGHTER than it
// should be — so the two are OR'd, never replaced.
const KNOWN_DESTRUCTIVE = ["delete_box"];

const INTENT_TITLES = {
  move_box: "Move a box",
  create_box: "Create a box",
  delete_box: "Delete a box",
};

const CONFIRM_LABELS = {
  move_box: "Confirm move",
  create_box: "Confirm create",
  delete_box: "Delete box",
};

// One question per clarification reason. The reason enum is pinned by a backend
// test (voice-session.test.mjs), so a value can only be added deliberately —
// and the fallback below keeps an unknown one from stranding a blank card.
const CLARIFICATION_COPY = {
  missing_box_reference: "Which box should this act on? Say it with the box number or name.",
  missing_destination_well: "Which well should it go to?",
  missing_create_details: "Where should the new box go? Say the puck and well.",
  ambiguous_action: "I couldn't tell what to do. Say move, create, or delete.",
  contradictory_values: "That command contradicted itself. Say it again in one go.",
  negated_or_hypothetical: "That sounded like something NOT to do, so nothing was done.",
  multiple_commands: "One command at a time, please. Say the first one again.",
  not_an_inventory_command: "That wasn't an inventory command, so nothing was done.",
  value_out_of_range: "Wells run 1 to 12. Say a well in that range.",
  unclear_audio: "I couldn't hear that clearly. Say it again.",
};

const STATE_WORDS = {
  starting: "Starting",
  // Startup is three separate waits. Naming them is the difference between a
  // permission prompt the user has not spotted and a dock that looks hung.
  requesting: "Requesting microphone",
  connecting: "Connecting",
  listening: "Listening",
  // The model asked a question and is waiting on an answer. Not "Listening":
  // it is the user's move, and the word should say so.
  replied: "Your turn",
  // "Understanding", not "Thinking". The dock's vocabulary names what the
  // system is doing to the user's sentence, not a state of mind it does not
  // have; and a person waiting on a parse wants to know their words were taken.
  thinking: "Understanding",
  checking: "Checking",
  previewing: "Check this",
  applying: "Applying",
  applied: "Done",
  refused: "Not possible",
  stopped: "Stopped",
  // Hold-to-talk with nothing held: the dock is open and the microphone is
  // captured, but nothing is being sent. Honest, and the call to action.
  ready: "Ready",
  // The resolver matched more than one puck or box; the chips answer it.
  whichOne: "Which one?",
};

// DESIGN.md section 2, "Voice extension": these map onto the existing five
// states and add no sixth.
const STATE_UI = {
  starting: "busy",
  requesting: "busy",
  connecting: "busy",
  listening: "busy",
  // `idle`, like a preview: nothing is in flight and the surface is waiting on
  // a person.
  replied: "idle",
  thinking: "busy",
  checking: "busy",
  previewing: "idle",
  applying: "busy",
  applied: "success",
  refused: "warning",
  stopped: "idle",
  ready: "idle",
  whichOne: "idle",
};

const METER_BARS = 12;

// The rates and the arithmetic live in `voiceDockWiring.costBreakdown`, which
// has a test. What is worth saying here is what the number on screen MEANS:
// display only, labelled "est.", rounded UP at a tenth of a cent so the dock
// can never understate what a session cost, and three decimals rather than two
// because at these rates every short session would otherwise round to one cent
// and a number that is the same for every session tells nobody anything.
//
// It is the whole bill now, not just the microphone: audio minutes, the parses
// the relay charged for, and the characters it read back. The first of those
// was the only one on screen until now, which made the other two free.

// Below this a pointer gesture on the sheet's handle was a tap, not a drag.
// Fingers move a few pixels during any deliberate tap; a handle that read 3 px
// of tremor as a drag would refuse to toggle for the people most likely to have
// it — cold hands in gloves.
const GRIP_TAP_SLOP_PX = 6;
// A replacement changes the meaning under the same physical button location.
// Hold taps for one second so a finger committed to the old card cannot confirm
// the new one.
const REPLACEMENT_TAP_GUARD_MS = 1000;
// The same rule for a SPOKEN confirmation, and longer, because the word has
// further to travel: said over the old card, it reaches the dock as a final
// transcript 0.2-1.5 s later. The session's own current-intent gate does not
// cover this — the replacement re-arms that gate with the NEW card's intent, so
// the late word passed it and applied a command the user never read.
const VOICE_REPLACEMENT_GUARD_MS = 1500;

function makeIdempotencyKey() {
  if (typeof crypto != "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `voice-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The three preferences, read once at construction.
 *
 * `readPrefs` already tolerates a storage object that throws on `getItem`, but
 * on a locked-down browser `window.localStorage` can throw on the PROPERTY
 * ACCESS itself, before there is anything to hand it — so the access is what is
 * wrapped here, and a refusal yields the defaults rather than a blank dock.
 */
function loadPrefs() {
  const micMode = defaultMicMode({
    coarsePointer: typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches,
    hover: typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover)").matches,
  });
  try {
    return readPrefs(window.localStorage, { micMode });
  } catch {
    return readPrefs(null, { micMode });
  }
}

export default {
  name: "VoiceDock",
  components: {
    VoiceStateLine,
    VoiceSettingsMenu,
    VoiceFocusView,
    VoiceContextChip,
    VoiceTranscript,
    VoiceCandidates,
    VoiceHoldButton,
  },
  emits: ["close", "follow-to-position"],
  // Manage Products provides its puck navigation; the Home page does not, and
  // there the "Show in rack" buttons are simply not offered.
  inject: { startAutoNavigate: { default: null } },
  data() {
    return {
      phase: "starting",
      transcript: "",
      // Recognition still in progress. Held apart from `transcript` so the
      // screen can show a guess as a guess.
      partialTranscript: "",
      // The model's last spoken reply, as text.
      assistantReply: "",
      preview: null,
      pendingCallId: null,
      pendingIntent: null,
      pendingFields: null,
      // Captured with the preview, not read again on confirm: navigation behind
      // the dock must not silently change which context gets re-resolved.
      pendingScreenPuck: null,
      // `{turnId, voiceSessionId}` for the spoken turn this card came from, so
      // the transcript log can link the preview and the receipt to the
      // utterance exactly rather than by matching arguments. Captured with the
      // proposal for the same reason `pendingScreenPuck` is: "Resume voice"
      // mints a second session with a second id, and the card belongs to the
      // turn that produced it, not to whichever session is alive at the tap.
      pendingTurnLink: null,
      // How THIS card may be confirmed, as the relay described it on the
      // proposal: 'voice' (a spoken window was opened for it) or 'tap' (a
      // shared-terminal lab, where a delete is confirmed by the button and the
      // word is never listened for). Held per card rather than read from a
      // preference, because it is the relay's decision about one command and
      // not a setting this dock owns.
      pendingConfirmMode: "voice",
      idempotencyKey: null,
      refusal: null,
      applied: null,
      busyAction: false,
      confirmTapGuard: false,
      confirmTapGuardTimer: null,
      replacementNeedsTapGuard: false,
      // When a REPLACEMENT card last rendered, as a wall clock. 0 means the
      // card on screen is a first card and nothing is guarded.
      replacedAt: 0,
      // An apply whose outcome we never learned. The card and its idempotency
      // key are kept so confirming again is the retry the server recognises —
      // and this is what lets that tap through a session the confirm itself
      // stopped.
      retryPending: false,
      // A re-parse of an edited sentence is in flight. The transcript is not
      // editable while it runs, and a second commit cannot start a second parse.
      reparsing: false,
      proposalGeneration: 0,
      checkingCallId: null,
      destructiveIntents: [...KNOWN_DESTRUCTIVE],
      meterLevel: 0,
      // Real transport facts, not derived from the phase word. The meter
      // caption must describe the microphone, and the microphone's state and
      // the label on the screen are two different things that can disagree.
      micOpen: false,
      micInfo: null,
      // A microphone being opened right now. Distinct from the `starting` and
      // `requesting` phases because the PRE-OPEN happens in the `ready` phase:
      // the panel is idle, no session exists, and the device is being handed
      // over anyway so that the first press does not have to wait for it.
      micOpening: false,
      // The browser refused the device. The panel stays open and says so, and
      // the microphone button is inert until the permission changes back —
      // pressing it could only produce the same notification again.
      micBlocked: false,
      // The `PermissionStatus` for the microphone, when this browser has one.
      // It is the only way the panel learns that a denial was reversed without
      // asking for the device again, which is a prompt of its own.
      micPermission: null,
      // A microphone opened AHEAD of the press, with no relay behind it and
      // nothing billed. `start()` adopts it as `session`; until then it is
      // deliberately not `session`, because every "is there a session" question
      // in this file — the footer, the state word, the first press — means "is
      // something listening on our account", and the answer to that is no.
      warmSession: null,
      // Set the instant this dock is torn down. `start()` awaits twice — the
      // token mint, then the socket — and during those gaps `this.session` may
      // not exist yet, so `unmounted`'s `session?.stop()` has nothing to stop.
      // Without this, closing mid-mint lets start() continue and open a
      // microphone on a dock that is already gone, with an upstream session
      // still billing and nothing holding a reference to it.
      teardown: false,
      micPaused: false,
      session: null,
      // The BILLED half, tracked apart from `session` because they are now two
      // lifetimes. `session` (and `sessionEnded`) is about the microphone graph
      // and the card: true from the first tap until the panel or a timer ends
      // it. `streamLive` is about the wire, and it goes false every time the
      // relay is torn down for being idle and true again on the next tap. The
      // state word, the meter caption's honesty and the card's own line all
      // turn on the second one; Confirm turns on neither, because applying is
      // plain HTTP.
      streamLive: false,
      // A reopen in flight. The mint and the handshake take 0.6-1.6 s, the word
      // says "Connecting" for them, and a second tap inside that window must not
      // ask the server for a second stream on one socket — it answers
      // VOICE_ALREADY_STREAMING, which would put an error card over a card the
      // user is reading.
      streamOpening: false,
      reducedMotion: false,
      lastMeterPaint: 0,
      // One object, one storage contract. `voiceRepliesOn` and `followMove` are
      // now computed views over this rather than two independently-persisted
      // booleans that could disagree with what the gear is showing.
      prefs: loadPrefs(),
      // Seconds BILLED, summed over every stream this panel-open has opened —
      // not over the stream that happens to be up. A clock that reset to 0:00
      // whenever the relay came back would hide the one thing this packet is
      // meant to make visible.
      elapsedStreamingSeconds: 0,
      // Seconds in which the microphone's own audio was actually forwarded, for
      // the tooltip's billed-against-spoken ratio and for the usage report.
      spokenSeconds: 0,
      // The other two thirds of the bill, as running totals for THIS session.
      // Dollars the relay charged for its parses (0 for a grammar hit and 0 for
      // the Codex subscription), and characters it charged for reading back.
      parseUsdTotal: 0,
      readbackChars: 0,
      usageTimer: null,
      speechToken: 0,
      // The relay read-back the dock is waiting on: its token, the sentence
      // (for the browser fallback) and the callback that opens the window.
      pendingSpeak: null,
      // The stop owed to a receipt that is being read back. An apply no longer
      // ends the session on its way past — that is what made "Done." arrive in
      // a different voice from the proposal — so the session outlives the apply
      // and this is what ends it when the receipt does. Null whenever no
      // receipt is in flight, and called at most once (`endReceiptSession`).
      receiptStop: null,
      sessionEnded: false,
      // The focus model of the command that just applied, drawn from the
      // refreshed store so the box is shown where it now is.
      appliedFocus: null,
      // "Which one?" — the resolver's candidates for the refused proposal, and
      // the proposal they belong to, so a tap can re-preview it.
      candidates: null,
      lastRefused: null,
      // The turn link the last apply was sent under, kept past `clearPending`.
      // The closing `voice:timing` is posted after the receipt has drawn, by
      // which point the card and its link are gone — and a timing row that
      // cannot name its turn is a row the log can only match by guesswork.
      lastTurnLink: null,
      // Hold-to-talk: true while the gate is open (button held, or unmuted in
      // hands-free mode).
      holding: false,

      // `{kind, deadlineAt, totalMs}` for whichever window is open, straight
      // from the session, or null. The state line draws it; nothing here
      // interprets it, because two clocks disagreeing about how long is left is
      // worse than one clock nobody can see.
      deadline: null,
      // Why the last session ended, in the user's words. Set on close from
      // `closed.kind`, cleared when a new session starts.
      exitCopy: "",
      // The one flag `resumeVoice` needs: re-arm the SAME card the moment the
      // replacement session says it is listening.
      resumeArmPending: false,

      // Geometry. Recomputed on resize; the panel's top tracks the search bar
      // it hangs under so the dock never covers the control that opened it.
      layout: "sheet",
      sheetSnap: "expanded", // opens tall (Andrew, 2026-09-18); the grip still collapses it to peek
      panelTop: 0,
      viewportHeight: 0,
      // Live drag state, null unless a pointer is down on the handle.
      grip: null,
      dragHeight: null,

      // Latency footer. Null means "not measured on this turn", which renders
      // as an em dash — never as zero.
      //
      // These are a PROJECTION of `this.timing`, not a second set of
      // measurements: `markTiming` recomputes all three from the ledger and
      // nothing else ever assigns them. The dock used to measure each one its
      // own way — a wall-clock subtraction for "Heard", the relay's own
      // server-side `latencyMs` for "Parsed", a second wall-clock subtraction
      // around the preview call for "Preview" — so the footer and the turn log
      // could report different numbers for the same turn, and one of the three
      // was not even a client measurement.
      heardMs: null,
      parsedMs: null,
      previewMs: null,
    };
  },
  computed: {
    /* --------------------------------------------------------- content */

    // The pucks that matter. A pending preview draws its own; an applied
    // receipt keeps the model built after the store refreshed.
    focusModel() {
      if (this.appliedFocus) return this.appliedFocus;
      if (!this.preview || !this.pendingIntent) return null;
      return makeFocusModel(this.preview, this.pendingIntent, this.locate);
    },
    canEditTranscript() {
      return (
        !this.busyAction &&
        !this.reparsing &&
        !!this.transcript &&
        !this.partialTranscript
      );
    },
    holdMode() {
      return this.prefs.micMode !== "free";
    },

    stateWord() {
      return STATE_WORDS[this.phase] || "Voice";
    },
    uiState() {
      return STATE_UI[this.phase] || "idle";
    },

    /* -------------------------------------------------------- preferences */

    // Read-only views, so there is exactly one place a preference is written:
    // `applyPrefs`. The old pair of `data` booleans could be assigned from
    // anywhere and then disagree with what the gear rendered.
    voiceRepliesOn() {
      return this.prefs.replies !== false;
    },
    followMove() {
      return this.prefs.follow === true;
    },

    /* ------------------------------------------------------------ geometry */

    isSheet() {
      return this.layout != "panel";
    },
    dockClass() {
      return {
        "sa-voice-panel": !this.isSheet,
        "sa-voice-sheet": this.isSheet,
        "sa-voice-sheet-dragging": !!this.grip,
        "sa-voice-mic-blocked": this.micBlocked,
      };
    },
    /**
     * Inline because both numbers are measured, not chosen: the panel hangs
     * from whatever the search bar's bottom edge turns out to be, and the
     * sheet's height is a fraction of a viewport only the browser knows.
     */
    dockStyle() {
      if (!this.isSheet) {
        return { top: `${this.panelTop}px`, width: `${PANEL_WIDTH_PX}px` };
      }
      return { height: `${this.sheetHeight}px` };
    },
    sheetHeight() {
      if (this.dragHeight != null) return this.dragHeight;
      return sheetHeightPx(this.sheetSnap, this.viewportHeight);
    },
    gripLabel() {
      return this.sheetSnap == "expanded"
        ? "Collapse the voice panel"
        : "Expand the voice panel";
    },

    /* -------------------------------------------------------------- footer */

    // Live means the session can still hear. It is the same fact the state word
    // reports, so the word and the buttons cannot disagree.
    sessionLive() {
      return isSessionLive({ session: this.session, sessionEnded: this.sessionEnded });
    },
    footerActions() {
      return footerButtons({
        preview: !!this.preview,
        live: this.sessionLive,
        retry: this.retryPending,
        holdMode: this.holdMode,
        destructive: this.isDestructive,
        confirmLabel: this.confirmLabel,
      });
    },
    actionDirection() {
      return footerDirection({
        destructive: this.isDestructive,
        layout: this.layout,
      });
    },

    /* ------------------------------------------------------------- latency */

    showLatency() {
      return (
        this.heardMs != null || this.parsedMs != null || this.previewMs != null
      );
    },
    latencyText() {
      return [
        `Heard ${this.latencySeconds(this.heardMs)}`,
        `Parsed ${this.latencySeconds(this.parsedMs)}`,
        `Preview ${this.latencySeconds(this.previewMs)}`,
      ].join(" · ");
    },
    intentTitle() {
      return INTENT_TITLES[this.pendingIntent] || "Confirm this command";
    },
    confirmLabel() {
      return CONFIRM_LABELS[this.pendingIntent] || "Confirm";
    },
    isDestructive() {
      return (
        !!this.pendingIntent &&
        this.destructiveIntents.includes(this.pendingIntent)
      );
    },
    // A card the relay will not take a spoken decision for. Read in four
    // places — the read-back's closing sentence, whether a window is opened at
    // all, the card's own line, and the refusal of a spoken confirmation that
    // arrives anyway — and computed from one field so those four cannot drift.
    tapOnlyConfirm() {
      return this.pendingConfirmMode == "tap";
    },
    gridsLost() {
      return Number(this.preview?.resolved?.nonEmptyGridCount) || 0;
    },
    // Labelled rows rather than the server's one-line summary: a scientist in
    // gloves verifies a destination well by finding the word "To", not by
    // parsing a sentence.
    previewRows() {
      const r = this.preview?.resolved;
      if (!r) return [];
      if (this.pendingIntent == "move_box") {
        return [
          { label: "Box", value: this.boxDisplayName(r) },
          {
            label: "From",
            value: `${r.sourcePuckLabel} well ${r.sourceWell}`,
          },
          {
            label: "To",
            value: `${r.destinationPuckLabel} well ${r.destinationWell}`,
          },
        ];
      }
      if (this.pendingIntent == "create_box") {
        return [
          { label: "Box", value: this.boxDisplayName(r) },
          {
            label: "Into",
            value: `${r.destinationPuckLabel} well ${r.destinationWell}`,
          },
        ];
      }
      if (this.pendingIntent == "delete_box") {
        return [
          { label: "Box", value: this.boxDisplayName(r) },
          { label: "Location", value: `${r.puckLabel} well ${r.well}` },
          {
            label: "Non-empty grids",
            value: String(this.gridsLost),
          },
        ];
      }
      // An intent this build does not know about still gets the server's own
      // sentence rather than an empty card.
      return [{ label: "Command", value: this.preview.summary || "" }];
    },
    // A partial supersedes the last completed transcript, because it IS the
    // sentence being said now — the previous one has already been answered.
    heardText() {
      return this.partialTranscript || this.transcript;
    },
    heardIsPartial() {
      return !!this.partialTranscript;
    },
    meterBars() {
      return METER_BARS;
    },
    meterLive() {
      return this.micOpen && !this.micPaused;
    },
    meterLabel() {
      return micMeterCaption({
        open: this.micOpen,
        opening: this.micOpening,
        phase: this.phase,
        paused: this.micPaused,
        preview: !!this.preview,
        label: this.micInfo?.label,
      });
    },
    // The gear's read-only line. Empty until a microphone has actually been
    // opened, which is the only moment either of its two facts exists.
    micSettingsText() {
      return micSettingsLine(this.micInfo);
    },
    // The three things this session has been billed for, each rounded up at the
    // mil so the parts add up to the whole on screen.
    usageCost() {
      return costBreakdown({
        seconds: this.elapsedStreamingSeconds,
        parseUsd: this.parseUsdTotal,
        ttsChars: this.readbackChars,
      });
    },
    usageText() {
      const seconds = Math.max(0, Number(this.elapsedStreamingSeconds) || 0);
      const minutes = Math.floor(seconds / 60);
      const remainder = String(seconds % 60).padStart(2, "0");
      // The clock still counts audio only — it is a clock, and the parse and
      // the read-back do not take the user's minutes. Only the money adds up.
      return `${minutes}:${remainder} · est. $${this.usageCost.total.toFixed(3)}`;
    },
    usageTitle() {
      const { voice, parse, readback } = this.usageCost;
      const money = `voice $${voice.toFixed(3)} · parse $${parse.toFixed(
        3
      )} · read-back $${readback.toFixed(3)}`;
      // The ratio the teardown exists to move: seconds paid for against seconds
      // anyone was talking. Only once there is speech to divide by — before
      // that it is not a small ratio, it is not a ratio.
      const ratio = billedVsSpoken({
        billedSeconds: this.elapsedStreamingSeconds,
        spokenSeconds: this.spokenSeconds,
      });
      if (ratio === null) return money;
      return `${money} · spoken ${this.spokenSeconds.toFixed(
        1
      )} s of ${this.elapsedStreamingSeconds} s billed (${ratio}×)`;
    },
    /**
     * The card's own line: what has happened, and what can still be done about
     * it. `cardStatusLine` owns the wording because a stream torn down for
     * being idle and a session that ran out are two different situations that
     * used to render the same two sentences.
     */
    cardStatus() {
      return cardStatusLine({
        sessionEnded: this.sessionEnded,
        streaming: this.streamLive,
        tapOnly: this.tapOnlyConfirm,
      });
    },
    // The open puck, published by PuckSection while it is mounted and cleared
    // on its unmount — so this is null whenever no puck is on screen.
    sourcePuckLabel() {
      return this.$store.state.voiceScreenContext?.puckLabel || null;
    },
    // Example phrasing uses what is actually on screen where possible, so the
    // sentence the user copies contains an identifier that will resolve.
    hintPuck() {
      return this.$store.state.voiceScreenContext?.puckLabel || "the puck";
    },
    hintBox() {
      return this.$store.state.voiceScreenContext?.boxLabels?.[0] || "its name";
    },
  },
  methods: {
    /* --------------------------------------------------------- content */

    locate(label, id) {
      return locatePuck(
        this.$store.state.dewarData,
        this.$store.state.unassignedPucks,
        { serial: label, id }
      );
    },

    showInRack({ puckLabel, well }) {
      if (typeof this.startAutoNavigate !== "function") return;
      this.startAutoNavigate(puckLabel, well, true);
    },

    onScan() {
      this.$store.dispatch("notify", {
        uiState: "idle",
        title: "Scanning arrives with the phone app.",
        detail: "Open the puck on screen for now, or say its name.",
        key: "voice|scan-stub",
      });
    },

    /**
     * The user fixed a word. Parse the corrected sentence over HTTP and feed
     * the result through the SAME proposal path a relayed parse takes. The
     * session's own raiseProposal is not used: a stopped session drops its
     * emits, and an edit is most useful precisely after the window closed.
     */
    async onReparse(text) {
      if (this.busyAction || this.reparsing) return;
      this.cancelSpeech();

      // The card the OLD sentence produced is dropped here, before the await,
      // not when the new one renders. A parse takes 1.5-4 s, and for all of it
      // the dock used to show the corrected sentence above a card describing
      // the command it replaced, with `busyAction` false and Confirm live: the
      // transcript and the card said different things and the button applied
      // the one nobody was reading. Dropped the way a superseding proposal
      // drops it, so that nothing confirmable exists until the new preview.
      const supersededCallId = this.pendingCallId || this.checkingCallId;
      if (this.preview || this.checkingCallId) {
        this.session?.respondToTool(
          supersededCallId,
          "Superseded by a newer command from the user."
        );
      }
      // The edit's OWN turn id, minted here rather than read off the answer.
      //
      // `/api/v1/voice/parse` records one `voiceturns` row per call and takes
      // the row's turn id from the request when the client sends one, falling
      // back to a random `http-` id when it does not; the response carries the
      // parse and nothing else (`voiceParseController.js`). So the only way the
      // dock can know which turn its edit became — and therefore the only way a
      // later preview can name the EDIT rather than the sentence it replaced —
      // is to choose the id first and send it.
      //
      // `editedFrom` is the turn being corrected. The card's own link answers
      // that when there is a card, and it is the better answer than the call
      // id: a card raised by a "which one?" chip carries a synthetic call id
      // and the SPOKEN turn in its link. The superseded call id is the fallback
      // for a preview still in flight, which has no link yet.
      //
      // Null when there was no card at all: the relay's `voice:final` carries a
      // turn id, but the transcript emit does not bring it this far, so an edit
      // made before any proposal landed can only be linked by the log's own
      // argument match, exactly as today.
      const editTurnId = "edit-" + Date.now();
      const editLink = turnLink({
        callId: editTurnId,
        voiceSessionId: this.session?.credentials?.voiceSessionId,
      });
      const editedFrom = turnLink({
        callId: this.pendingTurnLink?.turnId || supersededCallId,
      }).turnId;
      const parseLink = editedFrom ? { ...editLink, editedFrom } : editLink;
      if (this.preview) {
        // Same order as `onProposal`: the intent goes first, so a completed
        // "confirm" in the gap has nothing to classify against, and the
        // replacement's own guards are armed when it renders.
        this.session?.cancelConfirmation();
        this.replacementNeedsTapGuard = true;
      }
      // The generation moves HERE, with the card, and not when the replacement
      // renders. Clearing the card without moving it was the whole of the
      // defect: an edit made while the ORIGINAL preview's HTTP was still in
      // flight left that request holding the newest generation, so
      // `onProposal`'s "only the newest generation may render" check passed for
      // it, and the superseded command — well 7, corrected to well 9 seconds
      // earlier — drew a live card with Confirm enabled underneath the
      // corrected sentence. Same two lines `requestClose` uses, for the same
      // reason: everything that invalidates the card invalidates the answers
      // the card is waiting on.
      this.proposalGeneration += 1;
      this.checkingCallId = null;
      // Captured after the bump and compared after the await, so that a relay
      // proposal or a second edit arriving mid-parse discards THIS parse rather
      // than being overwritten by it.
      const generation = this.proposalGeneration;

      this.clearPending();
      this.candidates = null;
      this.lastRefused = null;
      this.refusal = null;

      this.transcript = text;
      this.phase = "thinking";
      // A corrected sentence is its own turn, and the sentence it replaces has
      // already been logged as one. Nothing was SPOKEN this time, so there is
      // no speech tail to measure — the commit is the moment the words became
      // final, and `sttFinal→proposal` then measures the re-parse round trip
      // the user is watching the spinner for.
      this.beginTimingTurn();
      this.markTiming("sttFinal");

      // Held for the whole round trip. It closes the transcript to editing
      // (`canEditTranscript`) and refuses a second commit, which used to start
      // a second parse racing the first for the same card.
      this.reparsing = true;
      try {
        let result;
        try {
          result = await this.$store.dispatch(
            "parseVoiceTranscript",
            {
              ...reparseRequest(text, this.sourcePuckLabel),
              link: parseLink,
              // Usually empty on a re-parse — only the commit has been marked —
              // so `withTiming` omits the key entirely rather than posting `{}`.
              timing: this.turnTiming(),
            }
          );
        } catch (err) {
          // A refusal belonging to a sentence that is no longer on screen is
          // as wrong as a card belonging to it.
          if (isStaleGeneration(generation, this.proposalGeneration)) return;
          this.refusal = {
            title: "Could not re-check that sentence.",
            detail:
              (err && err.serverMessage) ||
              (err && err.detail) ||
              "The server would not take it.",
            next: "Say it again, or use the screen.",
          };
          this.phase = "refused";
          return;
        }

        // The screen moved on while this parse was in flight — a relayed
        // proposal landed, the dock was closed, or the user edited again. The
        // newer thing owns the card now, and this answer is dropped whole.
        if (isStaleGeneration(generation, this.proposalGeneration)) return;

        const outcome = reparseOutcome(result, { callId: editTurnId });
        if (outcome.kind === "clarification") {
          this.onClarification(outcome.fields);
          return;
        }
        if (outcome.kind === "proposal") {
          await this.onProposal({
            callId: outcome.callId,
            intent: outcome.intent,
            fields: outcome.fields,
            // The pair the parse was SENT under. Re-deriving it here would read
            // the session id again, and a parse takes seconds — long enough for
            // the window to close and for "Resume voice" to open a second
            // session — so the preview would name a session the edit row was
            // never written under.
            link: editLink,
          });
          return;
        }
        this.refusal = {
          title: "Could not understand the edited sentence.",
          detail: "Nothing was changed.",
          next: "Say it again with the box, puck and well.",
        };
        this.phase = "refused";
      } finally {
        this.reparsing = false;
      }
    },

    /** A candidate chip was tapped: re-preview the refused proposal with it. */
    onChoose(choice) {
      if (!this.lastRefused || !this.candidates || this.busyAction) return;
      const { intent, fields, link } = this.lastRefused;
      const next = fieldsWithChoice(intent, fields, choice, this.candidates.kind);
      this.candidates = null;
      this.onProposal({
        callId: "choose-" + Date.now(),
        intent,
        fields: next,
        link,
      });
    },

    /** A structured ask from the parser, by relay or by re-parse. */
    onClarification(payload) {
      // A new question supersedes any stale outcome card — a retry after a
      // refusal commonly lands here, and the old "That command was refused"
      // must not keep rendering under the question that replaced it.
      this.refusal = null;
      this.applied = null;
      this.assistantReply =
        CLARIFICATION_COPY[payload && payload.reason] ||
        "Say that again with the box, puck and well.";
      this.partialTranscript = "";
      // "checking" means a proposal's preview call is still in flight; its
      // resolution owns the next phase, not this question.
      if (!this.preview && !this.busyAction && this.phase !== "checking")
        this.phase = "replied";
      this.speak(this.assistantReply);
    },

    /* ------------------------------------------------------ hold to talk */

    /**
     * The resting word, recomputed from the gate. Every site that moves the
     * gate ends here, so the four of them cannot disagree about what an open
     * or shut gate is called — which they did: a press promoted only "Ready",
     * a release demoted only "Listening", and the gear ran a third rule.
     *
     * Guarded down to the two resting words on purpose. The three startup
     * words belong to the session's own progress and only its `listening`
     * event may retire them; a press that relabelled "Requesting microphone"
     * as "Listening" would claim a microphone the browser has not granted.
     */
    updateRestingWord() {
      // A stream on its way up owns the word, and the word is "Connecting".
      // Measured 2026-09-17 (`probe-stream-life.mjs`): the local speaking
      // flourish emits `listening` off the microphone's own RMS, which reaches
      // here while the handshake is still running — and with no stream yet the
      // resting rule answered "Ready", so the 0.6-1.6 s reopen was reported as
      // an idle panel rather than as the wait it is.
      if (this.streamOpening) return;
      if (this.phase !== "listening" && this.phase !== "ready") return;
      this.phase = listeningPhase({
        holdMode: this.holdMode,
        holding: this.holding,
        phase: this.phase,
        live: this.sessionLive,
        // A session with no stream rests on Ready whatever the gate says.
        // "Listening" over a relay that was torn down would be the same claim
        // "Listening" over a microphone that was never opened used to be.
        streaming: this.streamLive,
      });
    },

    onHoldStart() {
      if (this.busyAction) return;
      // The browser refused the device. The button is inert rather than
      // apologetic: a press could only reach the same notification again.
      if (this.micBlocked) return;
      // Set BEFORE start(), and no longer overwritten by it. `start()` used to
      // assign `holding = !holdMode` — false in the mode where a button is
      // physically down — so the first hold of every session recorded with the
      // flag clear, the button reading "Hold to talk" and `aria-pressed`
      // false, for as long as the finger stayed on it.
      this.holding = true;
      if (!this.session || this.sessionEnded) {
        // First hold, or a hold after the session ran out: a fresh session,
        // keeping a pending card so a spoken confirm still has a target.
        this.startKeepingCard({ holding: true });
      } else if (!this.streamLive) {
        // The session is alive and holding the microphone; only the paid stream
        // was torn down for being idle. This is the cheap half of a start — no
        // permission prompt, no device handover — and it deliberately does NOT
        // go through `startKeepingCard`, which would stop the session and throw
        // away the microphone graph the whole packet exists to keep.
        this.reopenStream();
      }
      this.session?.setGate(true);
      this.updateRestingWord();
    },

    /**
     * Start a replacement session over the card that is already on screen.
     *
     * The one place three callers used to disagree. `resumeVoice` kept the card
     * AND re-armed it; `onHoldStart` and `applyGate` kept the card and stopped
     * there — so holding to talk over a stopped card, or switching to
     * hands-free over one, produced a session with no `confirmIntent` in it.
     * The card still said "Say confirm", the microphone was live, and the
     * spoken word was classified against nothing: no decision window ever
     * opened and nothing the user said could apply the command they were
     * looking at. `rearmResumedPreview` remains the single re-arm point; this
     * only decides that it is owed.
     *
     * Order matters twice. `resumeArmPending` is set AFTER the reset, never
     * before: `resetForNewSession` stops the old session, and a stop that is
     * not already closed runs `onSessionClosed`, which clears the flag — so a
     * flag set first would be cleared by the very call meant to make room for
     * it. And `holding` is set before `start()`, which no longer touches it.
     *
     * @param {{holding: boolean}} options what the microphone gate rests at.
     */
    startKeepingCard({ holding }) {
      // `!!this.preview` rather than a flat `true`: with no card there is
      // nothing to keep, and keeping "nothing" would leave a stale refusal
      // banner or a spent transcript on screen under a brand new session.
      this.resetForNewSession({ keepPending: !!this.preview });
      this.resumeArmPending = !!this.preview;
      this.holding = holding;
      this.start();
    },

    onHoldEnd() {
      this.holding = false;
      this.session?.setGate(false);
      this.updateRestingWord();
    },

    onToggleHold() {
      this.holding ? this.onHoldEnd() : this.onHoldStart();
    },

    onToggleFree() {
      if (this.micBlocked) return;
      this.holding = !this.holding;
      // Unmuting a hands-free session whose stream went idle is a request to be
      // heard again, and being heard needs a stream. Same cheap reopen the hold
      // and tap buttons make.
      if (this.holding && this.session && !this.sessionEnded && !this.streamLive) {
        this.reopenStream();
      }
      this.session?.setGate(this.holding);
      this.updateRestingWord();
    },

    /**
     * The gear changed the mic mode.
     *
     * Hold-to-talk closes the gate and rests on Ready; the next press opens it.
     *
     * Hands-free opens it — but only if there is something to open. Choosing
     * hands-free with no live session is the same intent as tapping Start
     * voice, because "starts on open" is what hands-free MEANS, so it starts
     * one. It used to open a gate on nothing and let the word become
     * "Listening" with `micOpen` false, `sessionLive` false and the footer
     * still offering Start voice: the dock claiming to hear through a
     * microphone that was never asked for.
     *
     * The pending card is kept across that start. Changing how the microphone
     * is held is not a decision about the command on screen.
     */
    applyGate() {
      this.holding = !this.holdMode;
      if (!this.holdMode && !this.sessionLive && !this.busyAction) {
        this.startKeepingCard({ holding: !this.holdMode });
        return;
      }
      // Hands-free over a live session whose stream had gone idle: the mode
      // means "open and listening", so the stream comes back the way a tap
      // would bring it back. Switching the other way needs nothing — the gate
      // shuts and the idle timer, if one is armed, closes it on its own.
      if (
        !this.holdMode &&
        !this.busyAction &&
        this.sessionLive &&
        !this.streamLive
      ) {
        this.reopenStream();
      }
      this.session?.setGate(this.holding);
      this.updateRestingWord();
    },

    /**
     * The apply landed and the store has been refreshed. Draw the receipt from
     * what the store says now, and hand the grid its follow target.
     */
    finishApplied(followTarget) {
      this.appliedFocus =
        this.preview && this.pendingIntent
          ? makeFocusModel(this.preview, this.pendingIntent, this.locate)
          : null;
      if (followTarget) this.$emit("follow-to-position", followTarget);
    },


    /**
     * The gear changed something. `writePrefs` returns what actually took
     * effect after normalisation — not what was passed in — so that is what is
     * held, and the menu re-renders from it.
     *
     * Turning replies OFF mid-card is the one change with a timing
     * consequence. The confirm window is armed when the read-back finishes, so
     * cancelling speech would otherwise leave a rendered card with no window at
     * all; `startConfirmWindow` is inert once started, so calling it here can
     * only open a window that was still waiting on a voice.
     */
    applyPrefs(next) {
      const before = this.prefs;
      let stored;
      try {
        stored = writePrefs(window.localStorage, next);
      } catch {
        stored = writePrefs(null, next);
      }
      this.prefs = stored;

      // The stream's idle policy reads this: with replies ON a pending card
      // holds the stream open for the spoken word the panel has promised, with
      // them OFF it does not. Pushed on every change, including the one below.
      this.session?.setRepliesOn(stored.replies !== false);

      if (before.replies && !stored.replies) {
        this.cancelSpeech();
        this.session?.startConfirmWindow({ repliesOn: false });
      }
    },

    clearConfirmTapGuard() {
      clearTimeout(this.confirmTapGuardTimer);
      this.confirmTapGuardTimer = null;
      this.confirmTapGuard = false;
    },

    /**
     * One replacement, two guards, one moment: this runs only when a card that
     * REPLACED another has actually rendered. The tap guard is a timer because
     * a disabled button has to re-enable itself; the voice guard is a stamp
     * because a spoken confirmation is only ever asked about once, when its
     * final transcript lands, and `voiceConfirmAllowed` does the arithmetic
     * then.
     */
    armConfirmTapGuard() {
      this.clearConfirmTapGuard();
      this.replacedAt = Date.now();
      this.confirmTapGuard = true;
      this.confirmTapGuardTimer = setTimeout(() => {
        this.confirmTapGuardTimer = null;
        this.confirmTapGuard = false;
      }, REPLACEMENT_TAP_GUARD_MS);
    },

    /**
     * Everything a second session needs cleared.
     *
     * Split in two by `keepPending`, and the split is the whole of "Resume
     * voice". A restart is a fresh start and throws the card away; a resume
     * keeps `preview`, `pendingIntent`, `pendingFields`, `pendingScreenPuck`,
     * `pendingTurnLink` and `idempotencyKey` EXACTLY as they are, because the
     * point of resuming is to confirm the very command that was on screen when
     * the window closed — same preview hash, same key, so the server sees a
     * retry rather than a second command.
     *
     * @param {{keepPending?: boolean}} [options]
     */
    resetForNewSession({ keepPending = false } = {}) {
      this.cancelSpeech();
      this.clearConfirmTapGuard();
      clearInterval(this.usageTimer);
      this.usageTimer = null;
      this.session?.stop("restart");
      this.session = null;
      this.proposalGeneration += 1;
      this.checkingCallId = null;
      this.replacementNeedsTapGuard = false;
      this.phase = "starting";
      this.deadline = null;
      if (!keepPending) {
        this.transcript = "";
        this.partialTranscript = "";
        this.assistantReply = "";
        this.clearPending();
        this.refusal = null;
        this.applied = null;
        this.appliedFocus = null;
        this.candidates = null;
        this.lastRefused = null;
        // The stopwatch goes with the card. A session kept across "Resume
        // voice" keeps its card AND its measurements, because the turn on
        // screen is the same turn; a session started without one is a clean
        // sheet.
        this.beginTimingTurn();
      }
      this.busyAction = false;
      this.meterLevel = 0;
      this.micOpen = false;
      this.micPaused = false;
      this.streamLive = false;
      this.streamOpening = false;
      this.elapsedStreamingSeconds = 0;
      this.spokenSeconds = 0;
      // All three totals reset together, and they reset on Resume voice too —
      // `startKeepingCard` comes through here with `keepPending`, and these sit
      // outside that branch deliberately. Resume voice keeps the CARD, but the
      // session under it is a new one that is billed from zero, and the footer
      // describes the session it is sitting on rather than the afternoon.
      this.parseUsdTotal = 0;
      this.readbackChars = 0;
      this.lastMeterPaint = 0;
      this.sessionEnded = false;
    },

    restartSession() {
      if (!this.sessionEnded || this.busyAction) return;
      this.resetForNewSession();
      this.holding = !this.holdMode;
      this.start();
    },

    /**
     * Re-arm the card that was on screen when the session ran out.
     *
     * A stopped session cannot apply anything, by tap or by voice — so the
     * footer offers this instead of Confirm, and this is the only thing that
     * puts Confirm back. The card, its hash and its idempotency key survive
     * untouched; the session underneath them is new.
     *
     * The re-arm itself waits for `listening`. `prepareConfirmation` installs
     * the intent the spoken "confirm" is classified against, and a session that
     * has not reached `listening` yet has no relay to classify anything.
     */
    resumeVoice() {
      if (!this.sessionEnded || this.busyAction || !this.preview) return;
      // A resumed hold-mode session stays gated until the next press, as it
      // did before; hands-free resumes with the gate open. Everything else this
      // used to do inline is `startKeepingCard`, which two other callers now
      // share — see the ordering note there.
      this.startKeepingCard({ holding: !this.holdMode });
    },

    /**
     * The replacement session is up and the kept card is still on screen. Put
     * the intent back, say it again, and let `announcePreview` open the window
     * — which is where the replies-off 20 s floor comes from, exactly as it did
     * the first time.
     */
    rearmResumedPreview() {
      this.resumeArmPending = false;
      if (!this.preview || !this.pendingIntent) return;
      this.phase = "previewing";
      this.session?.prepareConfirmation(this.pendingIntent);
      this.announcePreview();
    },

    boxDisplayName(resolved) {
      const title = resolved?.boxTitle;
      const label = resolved?.boxLabel;
      if (title && label && String(title) !== String(label)) {
        return `${title} · ${label}`;
      }
      return title || label || "Box";
    },

    boxSpokenName(resolved) {
      return resolved?.boxTitle || resolved?.boxLabel || "box";
    },

    proposalSpeech() {
      const r = this.preview?.resolved;
      if (!r) return this.preview?.summary || "Check this command.";
      const box = this.boxSpokenName(r);
      // How to confirm, said in the sentence that describes what would happen.
      // A tap-only card is listened to by nobody, so inviting the word would be
      // an instruction that does nothing: the user says "confirm", the relay
      // opened no window for it, and they say it again before reaching for the
      // screen. One phrase, chosen once, so the three intents cannot disagree.
      const how = this.tapOnlyConfirm
        ? "Tap the button to confirm."
        : this.pendingIntent == "delete_box"
          ? "Say confirm delete, or tap the button."
          : "Say confirm, or tap the button.";
      if (this.pendingIntent == "move_box") {
        return `Move ${box} from ${r.sourcePuckLabel} well ${r.sourceWell} to ${r.destinationPuckLabel} well ${r.destinationWell}. ${how}`;
      }
      if (this.pendingIntent == "create_box") {
        return `Create ${box} in ${r.destinationPuckLabel} well ${r.destinationWell}. ${how}`;
      }
      if (this.pendingIntent == "delete_box") {
        return `Delete ${box} from ${r.puckLabel} well ${r.well}. ${how}`;
      }
      return `${this.preview.summary || "Check this command."} ${how}`;
    },

    cancelSpeech() {
      this.speechToken += 1;
      this.pendingSpeak = null;
      this.session?.cancelSpeak();
      this.session?.holdMicForPlayback(false);
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* speech playback is optional */
      }
    },

    /**
     * Say a sentence, relay first.
     *
     * With spoken replies on, the sentence goes to the relay (`voice:say`) and
     * comes back as PCM the session plays on its own context: the server's
     * voice, the same on every device, and never a vendor key in the browser.
     * The browser's `speechSynthesis` is the fallback — when there is no live
     * relay to ask, when the relay answers `unavailable`, or when playback
     * fails. `onDone` fires once either way, which is what opens the confirm
     * window after a read-back and what ends the session after a receipt.
     *
     * The receipt used to be listed here as a sentence that ALWAYS took the
     * fallback, because the apply stopped the session before speaking it. It
     * no longer does (`confirmPreview`), so the receipt is now an ordinary
     * relay read-back and the whole exchange is in one voice.
     *
     * `kind` is the relay's label for the sentence, used for its own
     * accounting; it changes nothing about how it is spoken.
     */
    speak(text, onDone = null, kind = "clarification") {
      const sentence = String(text || "").trim();
      if (!sentence || !this.voiceRepliesOn) {
        if (onDone) onDone();
        return;
      }

      this.cancelSpeech();
      const token = this.speechToken;
      // The relay gets the sentence WHOLE or not at all. It used to get only
      // the first 220 characters of it: a long box title makes a proposal of
      // 278, the relayed text ended at "from", and the relay's `done` for that
      // truncation is an ordinary end — so the confirm window opened over a
      // read-back that had named neither the source nor the destination.
      // `speechRoute` sends anything longer to the browser voice, which has no
      // limit and says all of it, and the window opens on its end instead.
      if (
        speechRoute(sentence) === "relay" &&
        this.session &&
        this.session.say(kind, sentence)
      ) {
        this.pendingSpeak = { token, onDone, sentence };
        return;
      }
      this.speakInBrowser(sentence, onDone, token);
    },

    /** The relay's read-back settled: finish, or fall back to the browser voice. */
    onSpeakEvent(payload) {
      // Billed characters, counted BEFORE the pending guard. The relay charges
      // for what it synthesised, and it synthesised this whether or not the
      // dock was still waiting on it — a read-back cancelled by the user's next
      // sentence has already cost what it cost.
      const chars = Number(payload && payload.chars) || 0;
      if (chars > 0) this.readbackChars += chars;

      const pending = this.pendingSpeak;
      if (!pending || pending.token !== this.speechToken) return;
      const state = payload && payload.state;
      // `partial` says the relay's chunks had a gap: the user heard "Move
      // sample alpha from VP1 well 1 to" and nothing more. Treating that as a
      // finished sentence opened the confirm window over a destination that was
      // never spoken, so it takes the same road as `unavailable` — the browser
      // voice says the sentence, and the window opens on ITS end.
      const truncated = state === "ended" && !!(payload && payload.partial);
      if (state === "ended" && !truncated) {
        this.pendingSpeak = null;
        if (pending.onDone) pending.onDone();
      } else if (state === "unavailable" || truncated) {
        this.pendingSpeak = null;
        // A receipt waiting on this relay turn is not going to get one, so its
        // session is ended NOW rather than kept open for the length of a
        // browser read-back that needs no socket at all.
        //
        // Ordered before `speakInBrowser`, and that order is the whole of it:
        // stopping the session runs `onSessionClosed`, which cancels speech,
        // and a cancel that landed on an utterance already speaking would cut
        // the receipt off mid-word. Cancelling first and speaking second is
        // the same sequence `speak` itself uses.
        this.endReceiptSession();
        this.speakInBrowser(pending.sentence, pending.onDone, pending.token);
      }
      // `started` needs nothing; `cancelled` only ever follows cancelSpeech,
      // which has already retired this token.
    },

    speakInBrowser(sentence, onDone, token) {
      const available =
        typeof window != "undefined" &&
        window.speechSynthesis &&
        typeof window.SpeechSynthesisUtterance == "function";
      if (!available) {
        if (onDone) onDone();
        return;
      }
      const utterance = new window.SpeechSynthesisUtterance(sentence);
      let finished = false;
      const finish = () => {
        if (finished || token !== this.speechToken) return;
        finished = true;
        // `onend` and `onerror` share this, and only one of them is a sentence
        // that was heard out — but `speechSynthesis` reports a cut-off
        // utterance as `onend` as well, so the two cannot be told apart here.
        // The span is marked for both: a read-back that stopped early is still
        // the read-back this turn had.
        this.markTiming("ttsEnded");
        this.session?.holdMicForPlayback(false);
        if (onDone) onDone();
      };
      utterance.onstart = () => {
        if (token !== this.speechToken) return;
        // The fallback voice is still a read-back the person is listening to,
        // so it opens the same span the relay's would. First-write-wins in the
        // ledger means a relay turn that started and was then truncated keeps
        // its own start, and this only closes the sentence the user heard out.
        this.markTiming("ttsFirstAudible");
        this.session?.holdMicForPlayback(true);
      };
      utterance.onend = finish;
      utterance.onerror = finish;
      try {
        window.speechSynthesis.speak(utterance);
      } catch {
        finish();
      }
    },

    /**
     * Say the receipt for an apply, then end the session it was said on.
     *
     * The session is deliberately still alive at this point. `confirmPreview`
     * used to stop it before the apply, which meant `say()` returned false at
     * receipt time and "Done." always came out of the Mac's own voice — a
     * different speaker from the one that had just read the proposal, in the
     * middle of one exchange. The stop is owed either way; it is just owed
     * LATER, and this is where it is paid.
     *
     * Three ways the debt is settled, all of them through `endReceiptSession`
     * so none of them can double-stop:
     *   - the relay read-back ends: `onDone`, the ordinary case;
     *   - the relay says `unavailable` (or cuts the turn short): `onSpeakEvent`
     *     pays it before handing the sentence to the browser voice;
     *   - spoken replies are off, or there is nothing to say: `speak` calls
     *     `onDone` synchronously and the stop happens at once, exactly as the
     *     old code did.
     * A session that ended on its own during the apply — a timer, a dropped
     * socket — leaves nothing to stop: the sentence takes the browser voice as
     * it always did, and the stop lands on an already-closed session, which is
     * a no-op. The session is captured rather than read from `this` at call
     * time so a stop that arrives late cannot reach a session started since.
     */
    speakReceipt(text) {
      const session = this.session;
      this.receiptStop = () => session?.stop("confirmed");
      this.speak(text, () => this.endReceiptSession(), "receipt");
    },

    /** Pay the stop a receipt owes, once, whichever path gets here first. */
    endReceiptSession() {
      const stop = this.receiptStop;
      if (!stop) return;
      this.receiptStop = null;
      stop();
    },

    speakBanner(banner) {
      const text = `${banner?.title || ""} ${banner?.detail || ""}`.trim();
      this.speak(text.slice(0, 120), null, "refusal");
    },

    /**
     * Read the card back, then open the decision window.
     *
     * `repliesOn` is passed through because it is a READ-TIME question, not a
     * preference lookup the session could do for itself. With replies on this
     * callback fires when the speech ends and 12 s is a spoken decision's worth
     * of time; with them off `speak` calls back immediately, at render, and the
     * person is reading — so the session widens the window to the 20 s floor.
     */
    announcePreview() {
      this.speak(
        this.proposalSpeech(),
        // A tap-only card opens no window, and passes NO callback rather than
        // one that would turn out to be inert. The session holds no confirm
        // intent for such a card, so `startConfirmWindow` would return early
        // anyway — but a caller that relies on a callee's early return is a
        // rule that survives only as long as nobody rewrites the callee.
        //
        // `voiceRepliesOn`, not `prefs.replies`: `speak` decides whether to
        // say anything at all on the normalised view, and a window armed from
        // the raw value could be the 20 s reading floor over a sentence the
        // dock had just spoken.
        this.tapOnlyConfirm
          ? null
          : () =>
              this.session?.startConfirmWindow({
                repliesOn: this.voiceRepliesOn,
              }),
        "proposal"
      );
    },

    /** Seconds to one decimal, or an em dash for a turn that produced no number. */
    latencySeconds(ms) {
      if (!Number.isFinite(ms) || ms < 0) return "—";
      return `${(ms / 1000).toFixed(1)} s`;
    },

    /* --------------------------------------------------------- timing */

    /**
     * Stamp one mark and re-read the three the footer shows.
     *
     * EVERY timing write goes through here. The ledger is the single source of
     * truth for how long this turn took — the footer reads it, the preview body
     * carries it, the apply body carries it, and the `voice:timing` event sends
     * it — and a second place that assigned `heardMs` directly would be exactly
     * the split this packet removes.
     *
     * The three fields are recomputed rather than incremented because a mark
     * can complete a span the footer has been rendering as an em dash, and
     * because recomputing costs three object lookups.
     *
     * @param {string} key one of `voiceTiming.TIMING_MARKS`
     * @param {{replace?: boolean}} [options] only `speechEnd` passes `replace`
     */
    markTiming(key, options) {
      this.timing.mark(key, options);
      this.heardMs = this.timing.span("speechEnd", "sttFinal");
      this.parsedMs = this.timing.span("sttFinal", "proposal");
      this.previewMs = this.timing.span("proposal", "previewVisible");
    },

    /**
     * A new turn begins: drop every mark and blank the footer.
     *
     * A turn, for this purpose, is one sentence the user meant as a command and
     * everything that follows from it. The boundaries are deliberately few, and
     * each is a place the dock KNOWS the previous sentence is finished with:
     * the first word of a new utterance once the last one has been transcribed,
     * a proposal that replaces a card on screen, and a session restart.
     *
     * Not, notably, the final transcript: by then the speech marks of this same
     * turn have already been taken, and clearing them would throw away the one
     * span nothing else can measure.
     */
    beginTimingTurn() {
      this.timing.reset();
      this.heardMs = null;
      this.parsedMs = null;
      this.previewMs = null;
    },

    /** The snapshot to hang on a request body, or null when nothing is measured. */
    turnTiming() {
      const snapshot = this.timing.snapshot();
      return Object.keys(snapshot).length ? snapshot : null;
    },

    /**
     * The last word on a turn: the whole snapshot, after the receipt has drawn.
     *
     * By this point the preview body and the apply body have each carried what
     * was known at the time, and the server merges the three; this one is the
     * only carrier for the two spans that close the turn — how long the apply
     * took from the confirm, and how long the receipt took to reach the screen.
     *
     * It goes on the socket because there is no request left: the apply has
     * already returned. Sent best-effort and never surfaced — a turn whose
     * timing did not arrive is a missing row on an internal page, and telling
     * the user about it would be an interruption about nothing of theirs. A
     * server that does not know this event (every server until FIX-B9 lands)
     * ignores it, which is the ordinary socket.io behaviour for an unknown
     * message and costs one small frame.
     */
    sendTurnTiming() {
      const timing = this.turnTiming();
      if (!timing) return;
      if (!socket || !socket.connected) return;
      const link = this.pendingTurnLink || this.lastTurnLink;
      try {
        socket.emit("voice:timing", {
          ...(link && link.turnId ? { turnId: link.turnId } : {}),
          ...(link && link.voiceSessionId
            ? { voiceSessionId: link.voiceSessionId }
            : {}),
          timing,
        });
      } catch {
        // A socket that threw on emit is a socket that is gone. Nothing of the
        // user's is lost with it.
      }
    },

    /**
     * The footer's clock, over the SESSION rather than over one stream.
     *
     * It no longer zeroes what it finds. `capture-started` fires once per
     * stream, so a reopen runs this again — and resetting here would send the
     * clock back to 0:00 at the exact moment it is meant to show the user what
     * the teardown saved them. `session.elapsedSeconds()` already sums the
     * closed streams with the one that is running; zeroing belongs to
     * `resetForNewSession`, where a genuinely new session begins.
     */
    startUsageClock() {
      clearInterval(this.usageTimer);
      this.usageTimer = setInterval(() => {
        this.elapsedStreamingSeconds = this.session?.elapsedSeconds() || 0;
        this.spokenSeconds = this.session?.spokenSeconds() || 0;
      }, 1000);
      this.elapsedStreamingSeconds = this.session?.elapsedSeconds() || 0;
    },

    /**
     * Identifiers the user can actually see. The API budget is 20 terms, so the
     * open puck and its boxes are sent rather than the whole dewar — recognition
     * hints for things off screen would push the on-screen ones out.
     */
    keyterms() {
      const ctx = this.$store.state.voiceScreenContext || {};
      return [ctx.puckLabel, ...(ctx.boxLabels || [])].filter(Boolean);
    },

    /** Single place that pauses audio, so the meter caption cannot drift. */
    mute(paused) {
      this.micPaused = !!paused;
      this.session?.setMuted(!!paused);
    },

    /**
     * Esc steps back one level rather than closing outright: with a preview on
     * screen it cancels the preview, and only an already-idle session is closed.
     * Bound on the document because this dialog does not take focus away from
     * whatever the user was doing behind it.
     */
    onKeydown(ev) {
      if (ev.key != "Escape") return;
      // Bound on the document, so it hears Escape pressed in the page's own
      // search box — the input the microphone button sits beside — where
      // Escape means "clear this field" and meant, here, "throw away the card
      // you are reading" or "close the dock", with no message.
      if (isTypingTarget(ev.target)) return;
      if (this.busyAction) return;
      if (this.preview) {
        ev.stopPropagation();
        this.cancelPreview();
        return;
      }
      this.requestClose();
    },

    /**
     * Open the microphone before anyone presses anything.
     *
     * The panel is open because the user is about to talk, and the slow half of
     * starting is the device handover, not the relay: macOS handed Chrome an
     * iPhone's Continuity microphone from another room and the first press sat
     * on "Requesting microphone" for ten seconds (2026-09-08). Paying that when
     * the panel appears leaves the press with only a relay to start, which is
     * about half a second.
     *
     * Nothing is billed by this. There is no relay, no ledger row and no
     * upstream session; frames are captured and dropped. The visible cost is
     * Chrome's recording indicator while the panel is open, which is the
     * intended trade and is true — the microphone really is open.
     *
     * Only in the modes where the BUTTON is the way to start. Hands-free starts
     * a real session on open and owns its own microphone, and pre-opening a
     * second one for it would be two devices for one panel.
     */
    prewarmMicrophone() {
      if (this.teardown || this.micBlocked) return;
      if (!this.holdMode) return;
      // A live session already holds the device. Opening another would be a
      // second capture of the same room, and stopping the first would cut it.
      if (this.session && !this.sessionEnded) return;
      if (this.warmSession) return;

      this.micOpening = true;
      const warm = new VoiceSession({
        onEvent: this.onWarmEvent,
        mode: "hands_free",
        // NO ledger. This session opens the device when the PANEL opens, which
        // can be a minute before anybody presses anything — and a
        // `captureRequested` stamped there would make the first turn's capture
        // span the length of the pause before the press. The ledger is handed
        // over in `start()`, when this session is promoted to carry a relay, so
        // the span it then measures is the handshake the press is really
        // waiting on.
        // Shut, always. In hold and tap the first press opens it; nothing said
        // in front of an idle panel is captured for anyone.
        gateOpen: false,
      });
      this.warmSession = warm;
      warm.prepareMicrophone().catch((err) => this.onPrewarmFailed(err, warm));
    },

    /**
     * Events from a microphone that has no session behind it.
     *
     * Only four can arrive — `requesting-mic`, `mic-info`, `mic-open` and
     * `level` — and each means the same thing here as it does under a real
     * session, so they go to the same handler. `closed` is dropped: a pre-open
     * costs nothing, reports nothing and has no exit copy, and letting it
     * through would file a usage report for a session that never existed.
     */
    onWarmEvent(type, payload) {
      if (type == "closed") return;
      this.onSessionEvent(type, payload);
    },

    /**
     * The pre-open was refused.
     *
     * The panel stays open on Ready with its button inert, and the store's
     * existing one-line notification says why — the same sentence a denied
     * press has always produced, keyed so it cannot stack. `aborted` is not a
     * failure: it means this dock was torn down while the prompt was up.
     */
    onPrewarmFailed(err, warm) {
      if (this.warmSession === warm) this.warmSession = null;
      warm.stop("prewarm-failed");
      this.micOpening = false;
      if (this.teardown) return;
      if (err && err.voiceClass == "aborted") return;
      this.micBlocked = true;
      this.reportTransportFailure(err);
      this.watchMicrophonePermission();
    },

    /**
     * Notice that a denied microphone has been allowed again.
     *
     * Without this the only way back from a denial is to close the panel and
     * open it again, because the button that would retry is the button the
     * denial disabled. `permissions.query` is not universal — Safari has no
     * `microphone` name and rejects — so every part of this is optional and a
     * browser without it simply keeps the button inert until the panel is
     * reopened.
     */
    watchMicrophonePermission() {
      if (this.micPermission || !navigator.permissions?.query) return;
      navigator.permissions
        .query({ name: "microphone" })
        .then((status) => {
          if (this.teardown || !status) return;
          this.micPermission = status;
          status.onchange = () => {
            if (this.teardown || status.state != "granted") return;
            this.micBlocked = false;
            this.prewarmMicrophone();
          };
        })
        .catch(() => {});
    },

    /**
     * Hand over the pre-opened session, once.
     *
     * A closed one is not handed over: it has already released its tracks, and
     * adopting it would produce a session that looks warm and cannot hear.
     */
    takeWarmSession() {
      const warm = this.warmSession;
      this.warmSession = null;
      if (!warm || warm.closed) return null;
      return warm;
    },

    /**
     * Bring the paid stream back on the microphone that never went away.
     *
     * The expensive halves of a start have already been paid: the device is
     * open, the graph is capturing, the meter is live and the card — if there is
     * one — still holds its preview hash and its idempotency key. What is left
     * is a mint and the relay handshake, measured at 0.6-1.6 s.
     *
     * The state word says "Connecting" for that, not "Listening". Nothing is
     * reaching the relay yet, and a word that claimed otherwise would be the
     * same lie the three startup words were separated to stop telling. Audio
     * captured in the gap is held by the transport's bounded pre-buffer and
     * flushed on `voice:ready` — see `sendAudio`, which explains why holding is
     * the safe direction under Muse's ingress rule and dropping is not.
     *
     * A failure leaves the card alone. `session.start()` may end the session
     * itself on a mint refusal or a relay error, and `onSessionClosed` already
     * knows what to do with that: keep the preview, offer Resume voice. What
     * this must never do is `$emit("close")` the way the first `start()` does on
     * a mint failure, because there is a card on screen by now and closing the
     * dock would take it away over a token.
     */
    reopenStream() {
      if (this.teardown || this.busyAction || this.micBlocked) return;
      if (!this.session || this.sessionEnded) return;
      if (this.streamLive || this.streamOpening) return;

      // A press that has to bring the stream back is the first act of a new
      // turn, and the capture wait it is about to pay is the whole point of the
      // span. Marking it at the first partial instead would be too late: the
      // reset would land after `captureRequested` and wipe it.
      //
      // Not when a card is on screen: a press to say "confirm" over a torn-down
      // stream belongs to the turn already in hand, and resetting there would
      // throw away everything the preview body has not yet carried.
      if (!this.preview && !this.busyAction) this.beginTimingTurn();

      this.streamOpening = true;
      // Last stream's reason for stopping, gone the moment a new one begins.
      this.exitCopy = "";
      // Only a RESTING word is replaced. A card on screen owns "Check this" and
      // a question owns "Your turn"; relabelling either as "Connecting" would
      // describe the socket to someone who is being asked about their inventory.
      if (this.phase == "ready" || this.phase == "listening") {
        this.phase = "connecting";
      }
      this.session.setRepliesOn(this.voiceRepliesOn);

      const minted = this.$store.dispatch("mintVoiceSessionToken", {
        keyterms: this.keyterms(),
      });
      // Attached now: nothing awaits this until `reopenStream` does, and an
      // unattached rejection in that gap is an unhandled rejection.
      minted.catch(() => {});

      const session = this.session;
      session
        .reopenStream(minted)
        .catch((err) => {
          // `aborted` is this dock tearing down mid-handshake, and the session
          // has already released whatever it held.
          if (err && err.voiceClass == "aborted") return;
          // The store's own notification has already fired for a mint refusal.
          if (err && err.voiceClass != "mint") this.reportTransportFailure(err);
        })
        .finally(() => {
          if (this.session !== session) return;
          this.streamOpening = false;
          // A handshake that did not arrive leaves the panel resting rather
          // than stuck on "Connecting". A refused mint or a relay error ends
          // the session on its way past, and that rests on "Stopped" with the
          // card and its Resume voice underneath it.
          if (!this.streamLive && this.phase == "connecting") {
            this.phase = this.sessionEnded ? "stopped" : "ready";
          }
        });
    },

    async start() {
      this.sessionEnded = false;
      // No stream until `voice:ready` says so. Set before the first await, so a
      // `stream-open` arriving inside the handshake cannot be undone by it.
      this.streamLive = false;
      this.streamOpening = false;
      // `holding` is the CALLER's to set, and every caller sets it before this
      // runs: `onHoldStart` true, `restartSession`, `resumeVoice`, `applyGate`
      // and `mounted` `!holdMode`. Setting it here overwrote a hold that was
      // physically in progress, which is why the first press of every session
      // read as not-held for its whole length.
      // Last session's reason for stopping, gone the moment a new one begins.
      this.exitCopy = "";
      this.deadline = null;
      // Not awaited and never blocking: if the registry is unreachable the
      // fallback list still marks delete_box destructive.
      this.$store
        .dispatch("fetchVoiceIntents")
        .then((intents) => {
          if (!Array.isArray(intents)) return;
          const fromServer = intents
            .filter((i) => i && i.destructive)
            .map((i) => i.name);
          this.destructiveIntents = [
            ...new Set([...KNOWN_DESTRUCTIVE, ...fromServer]),
          ];
        })
        .catch(() => {});

      // Minted and asked for AT THE SAME TIME. The token is a network round
      // trip and the microphone is a permission prompt; neither needs the
      // other, and waiting for the first before starting the second put the sum
      // of the two between the user's tap and a live microphone.
      const minted = this.$store.dispatch("mintVoiceSessionToken", {
        keyterms: this.keyterms(),
      });
      // Attached now, not later: while the permission prompt is up nothing is
      // awaiting this promise, and a rejection with no handler on it is an
      // unhandled rejection. The real handling is the `mint` class below.
      minted.catch(() => {});

      // The microphone the panel pre-opened, promoted to the session that is
      // about to carry a relay. This is where the ten seconds went: the device
      // is already open, so `session.start()` finds the graph up and has only
      // the relay left to bring on.
      //
      // Constructed before either await either way, so an unmount during
      // startup always has something to stop. `start()` takes the mint as a
      // promise.
      const warm = this.takeWarmSession();
      if (warm) {
        // Its events were going to the pre-open handler, which drops `closed`.
        // From here this is an ordinary session and reports like one.
        warm.onEvent = this.onSessionEvent;
        // And the stopwatch, handed over HERE rather than at construction: see
        // `prewarmMicrophone`. The microphone this session already holds was
        // opened before the press, so the only capture wait left to measure is
        // the relay handshake — which `openRelay` stamps a moment from now.
        warm.timing = this.timing;
        // The gate the constructor would have been given. Hold and tap open it
        // on the press that got here; hands-free rests open.
        warm.setGate(!this.holdMode);
      }
      this.session =
        warm ||
        new VoiceSession({
          onEvent: this.onSessionEvent,
          // One hands-free session, gated by the button in hold-to-talk mode.
          mode: "hands_free",
          gateOpen: !this.holdMode,
          // Marks only: the session stamps the two ends of the capture wait and
          // the two ends of a relay read-back, and reads nothing back.
          timing: this.timing,
        });
      // Whether a pending card may hold the paid stream open waiting for a
      // spoken "confirm". It is a dock preference, so it is pushed in rather
      // than read out, and `applyPrefs` pushes it again when the gear moves it.
      this.session.setRepliesOn(this.voiceRepliesOn);

      try {
        await this.session.start(minted);
        // Closed while the socket was opening: the session is live now, so it
        // must be stopped explicitly rather than left for a hook that already ran.
        if (this.teardown) {
          this.session.stop("closed-during-start");
          return;
        }
        this.micOpen = true;
      } catch (err) {
        // `aborted` is not a failure: the transport threw it because THIS dock
        // was torn down mid-startup, and it already released whatever it held.
        // Telling the user "voice not started" for an action they themselves
        // took would be noise, and emitting close on an unmounted component is
        // pointless.
        if (err && err.voiceClass == "aborted") return;

        // The mint failed. `mintVoiceSessionToken` has already pushed the
        // notification, including the 402/403 distinction between "no minutes"
        // and "not in the beta" — and the transport reports this class ahead of
        // a microphone failure precisely so that a user who denied the
        // microphone AND has no minutes gets that one card rather than two
        // stacked cards saying different things.
        if (err && err.voiceClass == "mint") {
          this.$emit("close");
          return;
        }

        // The microphone is opened alongside the mint, so a socket failure can
        // leave a live capture behind. Release it here rather than relying on
        // unmount ordering — a hot mic in a shared lab is not a thing to leave
        // to a lifecycle hook.
        this.session.stop("failed-to-start");
        this.reportTransportFailure(err);
        this.$emit("close");
      }
    },

    reportTransportFailure(err) {
      const cls = err && err.voiceClass;
      if (cls == "micDenied") {
        this.$store.dispatch("notify", {
          uiState: "warning",
          title: "Voice not started.",
          detail: "Your browser did not allow microphone access.",
          next: "Allow the microphone for this site, then try again.",
          key: "voice|micDenied",
        });
        return;
      }
      if (cls == "unsupported") {
        this.$store.dispatch("notify", {
          uiState: "warning",
          title: "Voice not started.",
          detail: "This browser cannot open a microphone.",
          next: "Use Chrome or Safari on a secure connection.",
          key: "voice|unsupported",
        });
        return;
      }
      this.$store.dispatch("notify", {
        uiState: "danger",
        title: "Voice not started.",
        detail: "Could not connect to the voice service.",
        next: "Check your connection and try again.",
        key: "voice|transport",
      });
    },

    onSessionEvent(type, payload) {
      switch (type) {
        case "mic-info":
          this.micInfo = payload;
          break;

        // The graph is up and capturing, with or without a relay behind it.
        // This is the event the meter and its caption run on, so the caption
        // can name a device on an idle panel — which it now regularly does,
        // because the panel opens the microphone before the first press.
        case "mic-open":
          this.micOpen = true;
          this.micOpening = false;
          this.micBlocked = false;
          if (payload) this.micInfo = payload;
          break;

        case "level":
          this.paintMeter(payload);
          break;

        // Two named waits instead of one word covering all of startup. A
        // permission prompt the user has not noticed and a socket that will
        // never open look identical under "Starting".
        case "requesting-mic":
          if (this.phase == "starting") this.phase = "requesting";
          break;

        case "connecting":
          // The microphone really is open here — capture is running and frames
          // are being held for the socket — so the meter is allowed to move.
          this.micOpen = true;
          if (this.phase == "starting" || this.phase == "requesting") {
            this.phase = "connecting";
          }
          break;

        case "capture-started":
          this.startUsageClock();
          break;

        // The paid half is up. Said as its own event because "listening" is a
        // word about the user and this is a fact about the wire.
        case "stream-open":
          this.streamLive = true;
          this.streamOpening = false;
          break;

        // The paid half is gone and everything else stayed: the microphone, the
        // card, its hash, its key and the confirm window that belongs to it.
        // This is NOT a stop, and the difference is the whole packet — no
        // "Stopped:" line, no usage report, no Resume voice. The panel returns
        // to Ready with the card still up, and the next tap brings the stream
        // back through `reopenStream`.
        case "stream-closed":
          this.onStreamClosed(payload);
          break;

        // Which window is open, and until when. Null when nothing is armed.
        // Stored verbatim: the state line owns the projection onto a clock, and
        // a second opinion here would be a second clock.
        case "deadline":
          this.deadline = payload || null;
          break;

        case "listening":
          // A preview on screen owns the state word; a background turn ending
          // must not relabel it back to "Listening" under the user. Neither may
          // the microphone's own speaking flourish, which arrives on this same
          // event while a reopen's handshake is still running — see
          // `updateRestingWord`.
          if (!this.preview && !this.busyAction && !this.streamOpening) {
            this.phase = listeningPhase({
              holdMode: this.holdMode,
              holding: this.holding,
              phase: this.phase,
              live: this.sessionLive,
              // True by the time this runs: the session emits `stream-open`
              // from the same `voice:ready` action, and actions are performed
              // before emits.
              streaming: this.streamLive,
            });
          }
          // A resumed session says "listening" once the relay is up, which is
          // the first moment the kept card can be re-armed against it.
          if (this.resumeArmPending) this.rearmResumedPreview();
          break;

        case "thinking":
          if (!this.preview && !this.busyAction) this.phase = "thinking";
          break;

        // Recognition in progress. Cumulative, so it REPLACES rather than
        // appends, and it is never promoted to `transcript`: only the completed
        // event is the thing the command was built from, and the difference has
        // to stay visible.
        case "partialTranscript":
          // A partial arriving with the partial line empty is the FIRST word of
          // a new utterance. When the turn in hand has already been transcribed
          // and there is no card waiting on an answer, that word begins a new
          // turn and the ledger starts again. Guarded on `preview` and
          // `busyAction` because the other thing a person says into a live
          // session is "confirm", and a confirm belongs to the turn on screen.
          if (
            !this.partialTranscript &&
            !this.preview &&
            !this.busyAction &&
            this.timing.has("sttFinal")
          )
            this.beginTimingTurn();

          this.partialTranscript = payload || "";
          // The clock for "Heard". The relay does not report when the speaker
          // stopped, so the closest observable is the gap between the last
          // partial and the final that supersedes it — recognition's own
          // tail, which is the part the user experiences as a wait.
          //
          // With a card on screen the words being recognised are the DECISION,
          // not a command, so the first of them opens the confirm span instead.
          if (this.preview) this.markTiming("confirmSpeechStart");
          else if (!this.timing.has("sttFinal"))
            // `replace`, the one place the ledger allows it: every partial
            // supersedes the last, and the honest speech-end estimate is the
            // final partial before the transcript, not the first. Sealed once
            // the transcript has landed so a later utterance cannot move it.
            this.markTiming("speechEnd", { replace: true });
          break;

        case "transcript":
          // Recognition has settled. Which span this closes depends on what is
          // on screen: with no card it is the command that was spoken, with a
          // card up it is the answer to it. The dock cannot tell a confirm from
          // a replacement command at this point — only the relay's next message
          // can — so a replacement's own speech spans are dropped rather than
          // misattributed; `onProposal` starts that turn's ledger clean.
          if (this.preview) this.markTiming("confirmFinal");
          else this.markTiming("sttFinal");
          this.transcript = payload;
          this.partialTranscript = "";
          // A new user turn supersedes the answer to the previous one, so the
          // model's last question stops sitting under the words that answered
          // it.
          this.assistantReply = "";
          break;

        // The model answered in words instead of calling a tool — almost always
        // asking for the field the command was missing. Showing it is the whole
        // point of the conversational half: without it the user saw their
        // transcript, then nothing.
        case "assistantReply":
          this.assistantReply = payload || "";
          this.partialTranscript = "";
          if (!this.preview && !this.busyAction) this.phase = "replied";
          if (!this.preview) this.speak(this.assistantReply);
          break;

        case "proposal":
          this.onProposal(payload);
          break;

        // A structured ask from the clarification tool. Rendered on the same
        // surface as a spoken question because it IS one — the model just sends
        // it as data now instead of audio. The answer flow (mic open, "Answer
        // it, or voice stops shortly") is unchanged.
        case "clarification":
          this.onClarification(payload);
          break;

        case "model-error":
          // The model failing a turn is not the user's data failing. Say it
          // once, quietly, on the surface they are already looking at.
          this.refusal = {
            title: "That did not go through.",
            // The service's own wording when it sent one: "session expired" and
            // "audio too long" are different problems to the user, and the
            // generic sentence hid both behind the same line.
            detail:
              (payload && payload.message) ||
              "The voice service could not handle that request.",
            next: "Say it again, or stop voice and use the screen.",
          };
          this.phase = "refused";
          this.speakBanner(this.refusal);
          break;

        case "voiceConfirmation":
          this.handleVoiceConfirmation(payload);
          break;

        // A relay read-back started, ended, was cut, or could not be had.
        case "speak":
          this.onSpeakEvent(payload);
          break;

        case "closed":
          this.onSessionClosed(payload);
          break;

        default:
          break;
      }
    },

    /**
     * Meter paint. Peak-with-decay so the bars are readable rather than
     * flickering, and throttled under reduced motion.
     */
    paintMeter(rms) {
      const now = Date.now();
      if (this.reducedMotion && now - this.lastMeterPaint < 200) return;
      this.lastMeterPaint = now;

      // Speech rms sits well under 1; this maps a useful range onto the bars
      // without a log scale nobody would be able to read at a glance.
      const target = Math.min(METER_BARS, Math.round(rms * 42));
      this.meterLevel =
        target >= this.meterLevel
          ? target
          : Math.max(target, this.meterLevel - 1);
    },

    /**
     * A spoken decision arrived. Whose card it is, is settled before what it
     * says is read at all.
     *
     * Two ways it can belong to somebody else. The relay stamps a confirmation
     * with the epoch it opened the window for — the turn, and the hash of the
     * preview that was read back — and a hash that is not the card's names a
     * card this dock is no longer showing: superseded while the word was in
     * flight, or read out at another dock within earshot of this microphone.
     * And a tap-only lab opens no spoken window at all, so a confirmation
     * arriving under one of its cards is the relay disagreeing with itself.
     *
     * Either way the event is DROPPED, not consumed: no window is closed,
     * nothing is cancelled, the card stays exactly as armed as it was, and the
     * next "confirm" — the one said over this card, carrying this card's hash —
     * still applies. One warning, because a refusal nobody can see is
     * indistinguishable from a bug, and because on a relay that has not shipped
     * the epoch yet this line never runs (an unhashed confirmation is accepted,
     * as it is today).
     *
     * `cancel` is gated alongside `confirm`. A stale "no" that throws away a
     * card the user is still reading is the same defect pointing the other way,
     * and it is the direction that looks harmless right up until the person has
     * to say the whole sentence again.
     */
    async handleVoiceConfirmation(payload) {
      const action = payload && payload.action;

      const refusal = this.tapOnlyConfirm
        ? "this lab confirms by tap only"
        : confirmationMatchesCard({
              eventHash: payload && payload.previewHash,
              cardHash: this.preview && this.preview.previewHash,
            })
          ? null
          : "it names another card";
      if (refusal) {
        console.warn(
          `[voice] ignored a spoken "${action}": ${refusal}`,
          {
            turnId: (payload && payload.turnId) ?? null,
            previewHash: (payload && payload.previewHash) ?? null,
            cardHash: (this.preview && this.preview.previewHash) ?? null,
          }
        );
        return;
      }

      if (action == "wrong-phrase") {
        this.assistantReply = "Say confirm delete to apply.";
        this.speak(this.assistantReply);
        return;
      }
      if (action == "cancel") {
        this.cancelPreview();
        this.session?.stop("idle");
        return;
      }
      if (action == "confirm") {
        // The same predicate `confirmPreview` enforces at the seam, asked here
        // so the refusal is not silent. A user who said "confirm" over the card
        // they were reading has to hear that the card underneath it changed —
        // otherwise the word simply does nothing and they say it again into a
        // dock that has already moved on.
        if (
          !voiceConfirmAllowed({
            source: "voice",
            replacedAt: this.replacedAt,
            now: Date.now(),
            guardMs: VOICE_REPLACEMENT_GUARD_MS,
          })
        ) {
          this.assistantReply = "The card changed. Say confirm again.";
          this.speak(this.assistantReply);
          return;
        }
        await this.confirmPreview("voice");
      }
    },

    /**
     * A tool call arrived. This runs PREVIEW and nothing else. There is no
     * branch here, or anywhere below it, that reaches apply.
     */
    async onProposal({ callId, intent, fields, usd, link, confirmMode }) {
      // A proposal arriving over a card that is still on screen — or over a
      // preview still in flight — is a SECOND turn, and the ledger in hand
      // belongs to the first. Reset before stamping, which costs this turn its
      // own `speechEnd` and `sttFinal` (taken while the old card still owned
      // the screen, and therefore recorded against the confirm span instead):
      // absent is the honest answer, and a `sttFinal→proposal` measured from
      // the previous sentence would be a number that looks right and is not.
      if (this.preview || this.checkingCallId) this.beginTimingTurn();
      this.markTiming("proposal");

      // The relay's own `latencyMs` is no longer read here, and the parameter
      // is gone rather than left unused. It measured the SERVER's parse; the
      // footer's "Parsed" is the wait the person in front of the dock actually
      // had — `sttFinal→proposal`, marked above, on one monotonic clock, which
      // contains the relay's number plus the socket and the queue behind it.
      // Two numbers under one label was the split this packet closes, and the
      // server's figure still reaches the turn log from the server, where it is
      // measured.
      //
      // What the parse cost, added to the session's total. Zero for a grammar
      // hit and zero for a relay that does not price its parses; the footer
      // says "est." precisely because this is what the server claims it spent.
      if (Number.isFinite(usd) && usd > 0) this.parseUsdTotal += usd;

      if (this.busyAction) {
        // An apply has crossed the write seam and is no longer replaceable.
        this.session?.respondToTool(
          callId,
          "A previous proposal is still awaiting the user's confirmation on screen."
        );
        return;
      }

      const hadPreview = !!this.preview;
      const supersededCallId = this.pendingCallId || this.checkingCallId;
      if (hadPreview || this.checkingCallId) {
        this.session?.respondToTool(
          supersededCallId,
          "Superseded by a newer command from the user."
        );
      }
      if (hadPreview) {
        // Remove the old intent BEFORE previewing the replacement. A completed
        // "confirm" in this gap therefore has no confirmIntent to classify
        // against and cannot apply either the old or not-yet-rendered new card.
        this.session?.cancelConfirmation();
        // Arm only when the latest replacement RENDERS; preview validation may
        // take longer than the guard itself, and that time must not consume it.
        this.replacementNeedsTapGuard = true;
      }
      this.cancelSpeech();
      this.clearPending();
      this.assistantReply = "";

      this.candidates = null;
      this.lastRefused = null;
      this.appliedFocus = null;

      // Preview HTTP requests can overlap when speech corrections arrive fast.
      // Only the newest generation may render or surface a refusal.
      const generation = ++this.proposalGeneration;
      this.checkingCallId = callId;

      this.phase = "checking";
      this.refusal = null;
      this.applied = null;
      this.mute(true);

      const screenPuck = this.sourcePuckLabel;
      // Which spoken turn this is, read HERE and carried to the confirm rather
      // than read again at it. `link` is passed in by the two callers that
      // already know the answer: a re-parse names the edit turn it just wrote,
      // and a "which one?" chip re-previews the SAME utterance the chip is
      // answering — deriving that one from its synthetic call id would name a
      // turn that never existed and fall back to matching on arguments the chip
      // has just changed, which is exactly the mismatch this packet removes.
      const cardLink =
        link ||
        turnLink({
          callId,
          voiceSessionId: this.session?.credentials?.voiceSessionId,
        });
      let result;
      try {
        result = await this.$store.dispatch("previewVoiceCommand", {
          intent,
          fields: {
            ...fields,
            ...(screenPuck ? { screenPuck } : {}),
          },
          link: cardLink,
          // Everything measured up to the proposal: the capture wait, the tail
          // of recognition and the parse. The card has not drawn yet, so the
          // spans that follow it are genuinely absent rather than pending, and
          // the server merges the fuller snapshots that come after this one.
          timing: this.turnTiming(),
        });
      } catch (err) {
        if (generation != this.proposalGeneration) return;
        // No `previewVisible` mark: nothing was previewed. "Preview" in the
        // footer stays an em dash for a refused turn, which is what happened —
        // the old code printed the length of the refused round trip under a
        // label that promises a card the user never saw.
        this.checkingCallId = null;
        this.replacementNeedsTapGuard = false;
        this.session?.noteRefusal();
        this.refusal = {
          title: "That command was refused.",
          // The store only populates `serverMessage` for validation and
          // conflict classes, so this cannot echo authorization wording.
          // Falling back to the frontend's own copy when it is absent.
          detail:
            (err && err.serverMessage) ||
            (err && err.detail) ||
            "The server would not accept that.",
          // Fixed, not the store's `next`. A preview writes nothing and
          // triggers no resync, so the write vocabulary's "the screen has been
          // refreshed" would be false here. The next step after a refusal is
          // always the same: say it differently, or use the screen.
          next: "Say it again with a different well or puck, or use the screen.",
        };
        this.phase = "refused";
        // An ambiguity is answerable by a tap. Keep the refused proposal so
        // the chosen identifier can be written into it and re-previewed.
        this.candidates = candidatesFromRefusal(err);
        // The link travels with the refused proposal: a chip tapped on it is a
        // second preview of the same spoken turn.
        this.lastRefused = { intent, fields, link: cardLink };
        if (this.candidates) {
          this.phase = "whichOne";
          this.session?.armAnswerWindow();
        }
        this.mute(false);
        this.session?.respondToTool(
          callId,
          "Refused. Nothing was changed. Tell the user briefly and ask them to try again."
        );
        this.speakBanner(this.refusal);
        return;
      }

      if (generation != this.proposalGeneration) return;
      this.checkingCallId = null;

      this.pendingCallId = callId;
      this.pendingIntent = intent;
      this.pendingFields = fields;
      this.pendingScreenPuck = screenPuck;
      this.pendingTurnLink = cardLink;
      // Set BEFORE `prepareConfirmation` and before the read-back, because both
      // read it: one decides whether a spoken decision can be classified at all,
      // the other decides what the sentence tells the user to do.
      this.pendingConfirmMode = confirmModeOf(confirmMode);
      // Minted with the preview, not with the tap, so a RETRY of the same
      // confirmed command carries the same key and cannot apply twice.
      //
      // It is `busyAction` that stops a double-tap, not this key — the key's job
      // is the case where we never learned the outcome and the user confirms
      // again. That only works because the failure path now keeps it.
      this.idempotencyKey = makeIdempotencyKey();
      this.preview = result;
      this.phase = "previewing";
      // VISIBLE, not "responded". The span this closes is what the person
      // waited for, and the HTTP answer is not a card until Vue has drawn it —
      // on a phone in a cold room with a full grid behind the dock, that render
      // is not free. `nextTick` is the first moment the card is really there.
      this.$nextTick(() => this.markTiming("previewVisible"));
      if (this.replacementNeedsTapGuard) {
        this.replacementNeedsTapGuard = false;
        this.armConfirmTapGuard();
      }
      // A tap-only card is prepared WITHOUT a confirm intent: the timers are
      // cleaned up for it exactly as for any other card, but nothing in the
      // session can turn a spoken word into a decision about it.
      this.session?.prepareConfirmation(intent, {
        spoken: !this.tapOnlyConfirm,
      });
      this.mute(false);

      // The model is told what is on screen and, explicitly, that it is not
      // done. It must never announce a change that has not happened.
      this.session?.respondToTool(
        callId,
        `Proposed only: ${result.summary} The user must confirm on screen. Do not say it is done.`
      );
      this.announcePreview();
    },

    /**
     * The one path to a write. Reached by the rendered button or by the strict
     * completed-transcript event; both share this busyAction double-apply guard.
     */
    async confirmPreview(source = "tap") {
      if (
        !this.preview ||
        this.busyAction ||
        // A stopped session never applies BY VOICE — it has no microphone to
        // hear one — and never applies a fresh decision by tap either. The
        // footer has already replaced Confirm with Resume voice by this point,
        // so this is unreachable through the UI; it is here because this is the
        // ONE function that reaches `applyVoiceCommand`, and the invariant
        // belongs at the seam rather than only in the markup above it.
        //
        // `retryPending` is the single exception, and it is not a fresh
        // decision: the apply failed without an answer, THIS card's confirm
        // then ended the session as its receipt finished, and the retry carries
        // the same idempotency key and the same preview hash. It is a tap, on a
        // button the footer renders as "Confirm again", making an ordinary HTTP
        // call that never needed the socket. (For the second or two before that
        // stop lands the footer still says "Confirm", and a tap on it reaches
        // the same retry by the ordinary route — same key, same hash.)
        (this.sessionEnded && !(source == "tap" && this.retryPending)) ||
        (source == "tap" && this.confirmTapGuard) ||
        // A card the relay opened no spoken window for is not applied by a
        // spoken word, wherever that word came from. `handleVoiceConfirmation`
        // has already refused it once and the session holds no confirm intent
        // to classify it against; this is the same rule stated at the seam,
        // because the seam is where every other "may this apply" lives.
        (source == "voice" && this.tapOnlyConfirm) ||
        // And a spoken confirm inside a replacement's window is about the card
        // that WAS on screen, not the one that is.
        !voiceConfirmAllowed({
          source,
          replacedAt: this.replacedAt,
          now: Date.now(),
          guardMs: VOICE_REPLACEMENT_GUARD_MS,
        })
      )
        return;
      const followTarget =
        this.followMove && this.pendingIntent == "move_box"
          ? {
              puckLabel: this.preview.resolved.destinationPuckLabel,
              puckId: this.preview.resolved.destinationPuckId,
              well: this.preview.resolved.destinationWell,
            }
          : null;
      // The decision itself, stamped at the seam every confirm passes through
      // and after every guard above it: a tap the replacement guard refused is
      // not a confirm and must not open the span.
      this.markTiming("confirm");
      // Kept past `clearPending` so the closing `voice:timing` can still name
      // the turn it belongs to.
      this.lastTurnLink = this.pendingTurnLink;
      this.busyAction = true;
      this.clearConfirmTapGuard();
      this.phase = "applying";
      this.cancelSpeech();
      // Applying is plain HTTP and needs no microphone, so the capture is muted
      // for the length of it. The transport itself STAYS UP until the receipt
      // has been read back — see `speakReceipt`. It used to be stopped right
      // here, "so a click and a spoken confirmation have the same bounded
      // billing path", and they still do: the bound is now the receipt's end
      // plus the session's own timers, rather than the moment of the apply. A
      // second or two of a muted socket is what one voice for the whole
      // exchange costs.
      this.mute(true);
      // The decision has been made, so its window is over. The old code got
      // this for free: stopping the session took the 12 s confirm timer with
      // it. Left armed, that timer can now expire DURING the apply or during
      // the receipt — and it does not merely end the session, it ends it with
      // "Stopped: 12 s passed without a confirm. Nothing was changed." over a
      // command that was just applied, and cuts the receipt off mid-word on
      // its way past. Cancelling here rather than after the apply also puts
      // the whole HTTP call inside `busyAction`, which is what absorbs the
      // relay's own parse of a spoken "confirm" once the turn-key suppression
      // this clears is gone.
      this.session?.cancelConfirmation();

      try {
        await this.$store.dispatch("applyVoiceCommand", {
          intent: this.pendingIntent,
          fields: {
            ...this.pendingFields,
            ...(this.pendingScreenPuck
              ? { screenPuck: this.pendingScreenPuck }
              : {}),
          },
          expectedPreviewHash: this.preview.previewHash,
          idempotencyKey: this.idempotencyKey,
          // The SAME pair the preview was sent under, so the receipt lands on
          // the turn the user actually spoke. Not re-derived here: by now the
          // session may have ended, or be a second one opened by "Resume
          // voice".
          link: this.pendingTurnLink,
          // The turn so far, including the card's own spans: how long the
          // preview took to draw, how long the read-back ran, and how long the
          // spoken confirm took to come back. `confirm→applied` cannot be in
          // here — this request is the thing being measured — and rides out on
          // the `voice:timing` that closes the turn.
          timing: this.turnTiming(),
        });

        // The write landed and the store has been refreshed inside the action.
        // Stamped before the receipt is built, so the span is the apply and not
        // the apply plus the drawing of its receipt, which is the span after it.
        this.markTiming("applied");
        this.session?.noteApplied();
        this.applied = {
          title: this.appliedTitle(),
          detail: this.preview.summary || "",
        };
        this.phase = "applied";
        this.finishApplied(followTarget);
        this.clearPending();
        this.speakReceipt("Done.");
      } catch (err) {
        const cls = (err && err.voiceClass) || "server";
        // `offline` and `server` mean WE NEVER LEARNED THE OUTCOME — the request
        // may well have committed and only the response was lost. On a phone in
        // a cold room, which is the device this dock exists for, that is the
        // likely failure. Saying "Box not saved." there states as fact the one
        // thing we do not know, and on a system whose whole premise is that the
        // user knows what happened to their sample, telling them the opposite of
        // the truth is the defect.
        const outcomeUnknown = cls == "offline" || cls == "server";

        this.$store.dispatch("notify", {
          uiState: err && err.uiState == "danger" ? "danger" : "warning",
          title: outcomeUnknown ? "Not sure that saved." : "Box not saved.",
          // `serverMessage` is only ever set for validation and conflict
          // classes (DESIGN.md section 5, "invalid": server message, else the
          // frontend's own wording).
          detail: outcomeUnknown
            ? "The server did not answer, so this may or may not have been applied."
            : (err && err.serverMessage) ||
              (err && err.detail) ||
              "The server would not accept that.",
          next: outcomeUnknown
            ? "Check the screen. If it did not happen, confirm again — retrying is safe."
            : (err && err.next) || "Check the screen, then try again.",
          key: `voice|apply|${cls}`,
        });

        if (outcomeUnknown) {
          // Keep the preview and — crucially — the SAME idempotency key, so
          // confirming again is a genuine retry the server can recognise rather
          // than a second, separate command. Clearing it here is what made the
          // retry the key exists for impossible.
          //
          // And arm the retry, which is what makes the offer real. This confirm
          // ends the session as soon as its receipt has been read back, so
          // without this the notification's "confirm again — retrying is safe"
          // asked for a whole new billed relay session to press a button that
          // makes plain HTTP. Cleared by `clearPending`, so it cannot outlive
          // the card it belongs to.
          this.retryPending = true;
          this.phase = "previewing";
        } else {
          // "Stopped", not a ternary on `sessionEnded`. That test used to be
          // decided by a stop this function had already made on its way past;
          // the stop now happens a second later, when the receipt ends, and
          // asking the flag here would answer "listening" and leave that word
          // standing over a session that is about to be gone.
          this.phase = "stopped";
          this.clearPending();
        }
        this.speakReceipt("That didn't apply. Check the screen.");
      } finally {
        this.busyAction = false;
        this.mute(false);
        // The turn is over, whichever way it went, and the surface that says so
        // is on screen one tick from now — the receipt, or the banner standing
        // in for it. `applied→receiptVisible` therefore exists only on the path
        // where something really was applied, which is correct: there is no
        // receipt for a write that did not happen.
        //
        // This is the ONLY send that can carry `confirm→applied`, so it runs on
        // the failure path too: a turn that took nine seconds to fail is the
        // most interesting row on the page.
        this.$nextTick(() => {
          this.markTiming("receiptVisible");
          this.sendTurnTiming();
        });
      }
    },

    appliedTitle() {
      if (this.pendingIntent == "delete_box") return "Box deleted.";
      if (this.pendingIntent == "create_box") return "Box created.";
      if (this.pendingIntent == "move_box") return "Box moved.";
      return "Command applied.";
    },

    cancelPreview() {
      if (this.busyAction) return;
      this.candidates = null;
      this.lastRefused = null;
      const callId = this.pendingCallId;
      this.cancelSpeech();
      this.session?.cancelConfirmation();
      this.clearPending();
      this.phase = this.sessionEnded ? "stopped" : "listening";
      // And then the resting rule, which knows about the gate and about a
      // stream that was torn down while the card was up. Without it a cancel
      // left "Listening" over a relay that is not there.
      this.updateRestingWord();
      this.mute(false);
      this.session?.respondToTool(
        callId,
        "The user cancelled. Nothing was changed."
      );
    },

    clearPending() {
      this.preview = null;
      this.pendingCallId = null;
      this.pendingIntent = null;
      this.pendingFields = null;
      this.pendingScreenPuck = null;
      this.pendingTurnLink = null;
      // Back to what every relay says by saying nothing. A card that leaves the
      // screen takes its confirm mode with it, so a tap-only delete cannot
      // leave the next move card refusing the spoken confirm it was promised.
      this.pendingConfirmMode = "voice";
      this.idempotencyKey = null;
      // Both belong to the card, not to the dock. This one function is every
      // way a card leaves the screen — cancelled, applied, superseded by a new
      // proposal, replaced by a re-parse — so clearing them here is what stops
      // a retry offer outliving the command it was offered for, and stops one
      // replacement's stamp guarding an unrelated card later.
      this.retryPending = false;
      this.replacedAt = 0;
    },

    /* ---------------------------------------------------- footer dispatch */

    /**
     * Disabled state, per button.
     *
     * `busyAction` covers an apply in flight everywhere. The replacement tap
     * guard applies to Confirm alone: it exists because a NEW card has just
     * taken the same physical position as the one a finger was already
     * committed to, and it must never freeze the way out.
     */
    isActionDisabled(button) {
      if (button.action == "confirm") {
        return this.busyAction || this.confirmTapGuard;
      }
      if (button.action == "close") return false;
      return this.busyAction;
    },

    runAction(action) {
      if (action == "confirm") return this.confirmPreview("tap");
      if (action == "cancel") return this.cancelPreview();
      if (action == "resume") return this.resumeVoice();
      if (action == "start") return this.restartSession();
      // "stop" and "close" are the same act on a live session: end it and let
      // the control that opened the dock take the surface away.
      return this.requestClose();
    },

    /* ------------------------------------------------------------ geometry */

    /**
     * Panel or sheet, and where the panel starts.
     *
     * The top edge is measured from the search bar rather than assumed, because
     * that bar is sticky, carries the button that opened this dock, and grows
     * on the phone layouts where the global font clamp is largest. A dock
     * covering its own trigger would be a surface the user cannot dismiss the
     * way they opened it.
     */
    measureViewport() {
      const width = window.innerWidth || 0;
      this.layout = layoutFor(width);
      this.viewportHeight = window.innerHeight || 0;

      let top = 0;
      try {
        const bar = document.getElementById("searchbarheight");
        if (bar) top = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
      } catch {
        /* No search bar on this page: the panel starts at the top. */
      }
      this.panelTop = top;
    },

    onViewportChange() {
      // A resize mid-drag would fight the pointer for the same height.
      this.grip = null;
      this.dragHeight = null;
      this.measureViewport();
    },

    setSnap(snap) {
      this.sheetSnap = snap;
      this.dragHeight = null;
    },

    onGripDown(event) {
      if (!this.isSheet) return;
      this.grip = {
        id: event.pointerId,
        y: event.clientY,
        height: this.sheetHeight,
        moved: 0,
      };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* Capture is an optimisation; the listeners still fire without it. */
      }
    },

    onGripMove(event) {
      const grip = this.grip;
      if (!grip || event.pointerId != grip.id) return;
      // Upwards is taller: the sheet is anchored to the bottom of the screen,
      // so a thumb moving towards the top is asking for more of it.
      const delta = grip.y - event.clientY;
      grip.moved = Math.max(grip.moved, Math.abs(delta));
      const min = sheetHeightPx("peek", this.viewportHeight);
      const max = sheetHeightPx("expanded", this.viewportHeight);
      this.dragHeight = Math.min(max, Math.max(min, grip.height + delta));
    },

    onGripUp(event) {
      const grip = this.grip;
      if (!grip || (event && event.pointerId != grip.id)) return;
      this.grip = null;

      // A tap toggles. Anything longer than the slop was a drag, and lands on
      // whichever stop it finished nearest.
      if (grip.moved <= GRIP_TAP_SLOP_PX) {
        this.dragHeight = null;
        this.setSnap(nextSnap(this.sheetSnap, this.sheetSnap == "peek" ? "up" : "down"));
        return;
      }

      const height = this.dragHeight == null ? grip.height : this.dragHeight;
      const fraction = this.viewportHeight ? height / this.viewportHeight : SHEET_SNAPS.peek;
      this.setSnap(snapForFraction(fraction));
    },

    /** The handle is a button, so it answers the keys a button answers. */
    onGripKeydown(event) {
      if (event.key == "ArrowUp") {
        event.preventDefault();
        this.setSnap(nextSnap(this.sheetSnap, "up"));
        return;
      }
      if (event.key == "ArrowDown") {
        event.preventDefault();
        this.setSnap(nextSnap(this.sheetSnap, "down"));
        return;
      }
      if (event.key == "Enter" || event.key == " " || event.key == "Spacebar") {
        event.preventDefault();
        this.setSnap(
          nextSnap(this.sheetSnap, this.sheetSnap == "peek" ? "up" : "down")
        );
      }
    },

    requestClose() {
      // An apply already in flight is not cancelled by closing: the request is
      // with the server and the notification will report how it ended.
      this.cancelSpeech();
      this.clearConfirmTapGuard();
      this.proposalGeneration += 1;
      this.checkingCallId = null;
      this.replacementNeedsTapGuard = false;
      this.session?.stop("user");
      // The pre-opened microphone closes with the panel, exactly as a session's
      // does. A dock that is open and idle keeps it; a dock that is gone must
      // not, and `teardown` stops the close from opening another.
      this.teardown = true;
      this.warmSession?.stop("user");
      this.warmSession = null;
      this.$emit("close");
    },

    /**
     * The relay stream was torn down for being idle. The session was not.
     *
     * Everything that costs money has stopped and everything the user can see
     * has stayed: the microphone graph is still capturing (so the meter still
     * moves and the next tap has no device to wait for), the card is still on
     * screen with its preview hash and its idempotency key, and the confirm
     * window that belongs to the card is still counting down. Confirm is a live
     * button throughout — applying is plain HTTP and never needed the socket.
     *
     * So there is no exit copy. "Stopped: …" names a window that ran out and
     * ended a session, and nothing here ended: the panel returns to Ready, the
     * card's own line changes to say a spoken confirm now needs a tap first, and
     * the footer is untouched because `sessionLive` is still true.
     *
     * The usage report is deliberately NOT filed here. It names one
     * `voicesessions` row and carries the session's cumulative outcome; filing
     * it per stream would count previews and applies once per teardown on the
     * admin funnel. The relay finalises each stream's own row server-side, and
     * the dock reports once, on `closed`.
     */
    onStreamClosed(payload) {
      this.streamLive = false;
      this.streamOpening = false;
      this.elapsedStreamingSeconds = Math.max(
        0,
        Number(payload && payload.billedSeconds) || 0
      );
      this.spokenSeconds = Math.max(
        0,
        Number(this.session?.spokenSeconds?.()) || this.spokenSeconds
      );
      // Nothing is being recognised on a stream that is gone; leaving a partial
      // italicised on screen would say otherwise. The completed transcript and
      // the card stay exactly as they are.
      this.partialTranscript = "";
      this.meterLevel = 0;
      // The gate flag decides whether the next tap STARTS or ENDS a hold, and
      // there is nothing to hold open now. Same reasoning as `onSessionClosed`:
      // a flag left true costs the user a tap that looks like nothing happening.
      this.holding = false;
      this.session?.setGate(false);
      // Only the resting words move. A card keeps "Check this", a question
      // keeps "Your turn"; `listeningPhase` leaves both alone.
      this.updateRestingWord();
    },

    onSessionClosed(closed) {
      const { reason, seconds, outcome, billedSeconds, spokenSeconds } =
        closed || {};
      this.cancelSpeech();
      this.sessionEnded = true;
      this.streamLive = false;
      this.streamOpening = false;
      // The session cannot emit after it closes, so nothing else will ever
      // clear this — and a ring left draining over a dead session is a
      // countdown to nothing. Clearing it here is what stops the state line
      // outliving the thing it is reporting on.
      this.deadline = null;
      // Which of the five windows ran out, in the user's words. `closed.kind`
      // is null for every close that was not a timer — a dropped socket, a tap
      // on Stop, a confirm — and those get no sentence at all rather than a
      // borrowed one.
      this.exitCopy = exitCopyFor(
        closed,
        confirmWindowMs({ repliesOn: this.voiceRepliesOn })
      );
      this.resumeArmPending = false;
      clearInterval(this.usageTimer);
      this.usageTimer = null;
      // The whole panel-open, not the last stream: the clock is a session total
      // and `seconds` is one row's. `billedSeconds` falls back to `seconds` for
      // a session that never reopened anything, which is every session before
      // this packet.
      this.elapsedStreamingSeconds = Math.max(
        0,
        Number(billedSeconds ?? seconds) || 0
      );
      this.spokenSeconds = Math.max(0, Number(spokenSeconds) || 0);
      this.micOpen = false;
      this.micPaused = false;
      this.meterLevel = 0;
      // A stopped session holds nothing, so the gate flag must not still say it
      // does. In tap mode `holding` is what decides whether the next tap STARTS
      // a session or ends a hold — so a session that ran out with the gate open
      // left the button reading "Tap when done" over a dead session, and the
      // next tap spent itself closing a gate that no longer existed. Two taps to
      // speak again, the first of which looked like nothing happening.
      // Every caller of `start()` sets this for itself afterwards, in that
      // order, so clearing it here cannot fight a session that is starting.
      this.holding = false;
      // Whatever was half-recognised when the socket went is not going to be
      // finished now, and leaving it italicised on screen would say recognition
      // is still running. The last completed transcript stays.
      this.partialTranscript = "";

      this.$store.dispatch("reportVoiceSession", {
        id: this.session?.credentials?.voiceSessionId,
        // The LAST stream's seconds, because the id above is the last stream's
        // row. The streams that closed earlier have rows of their own that the
        // relay finalised on their `voice:stop`.
        reportedSeconds: seconds,
        // Seconds anyone was actually talking, so the admin page can show
        // billed against spoken. The store's action does not forward this key
        // yet — it lands with FIX-B9, which teaches the PATCH `timing`; sending
        // it now costs nothing and means no second change here.
        spokenSeconds,
        outcome,
      });

      if (reason == "transport-lost") {
        // DESIGN.md section 9: transport loss is `danger`.
        this.$store.dispatch("notify", {
          uiState: "danger",
          title: "Voice stopped.",
          detail: "The connection to the voice service was lost.",
          next: "Start voice again to carry on.",
          key: "voice|transport-lost",
        });
        // A card on screen survives a dropped socket, exactly as it survives a
        // timer running out (§2.1: "a live card after stop shows Not applied
        // and one Resume voice action"). Closing the dock here threw away the
        // preview, its hash and its key over a second of lost Wi-Fi, leaving a
        // toast as the only trace and the whole command to say again — and
        // confirming never needed the socket in the first place. The toast
        // still fires: the connection really did go, and that is worth saying.
        if (this.preview) {
          this.phase = "stopped";
          this.prewarmMicrophone();
          return;
        }
        this.$emit("close");
        return;
      }

      if (reason == "idle" || reason == "timeout") {
        this.phase = "stopped";
        // The panel is still open, so the next press gets the same head start
        // the first one did. This is the second half of the pre-open: without
        // it only the FIRST press of a panel's life was instant.
        this.prewarmMicrophone();

        // If a preview is on screen the user is MID-DECISION, and closing the
        // dock would take it away from them. The old comment here claimed
        // "nothing of the user's was lost", which was simply untrue in this case:
        // they are reading a confirmation and about to tap it.
        //
        // Confirming never needed the session. `applyVoiceCommand` is an ordinary
        // HTTP call and does not touch the socket, so ending the session stops
        // the billing WITHOUT costing the decision. Keep the card; the mic is off
        // and the state word says so.
        if (this.preview) return;

        // Nothing pending: no error, nothing lost. Keep the dock open so the
        // person can start a fresh billed session or close it explicitly.
        return;
      }

      // The last two stops that leave the panel on screen: a confirmed command,
      // whose receipt the user usually follows with another sentence, and a
      // relay that refused to start, which the user retries from the same
      // panel. `restart` is deliberately NOT here: `start()` is already running
      // behind it and opens the device itself, so a pre-open racing it would be
      // two handovers for one press.
      if (reason == "confirmed" || reason == "start-failed") {
        this.prewarmMicrophone();
      }
    },
  },

  watch: {
    "prefs.micMode": "applyGate",
  },

  /**
   * The turn stopwatch, created before anything can mark it.
   *
   * Held as a plain instance property rather than in `data()` on purpose. It is
   * not state the template renders — the three numbers it renders are projected
   * out of it by `markTiming` — and putting a stopwatch behind Vue's reactivity
   * proxy would make every mark a dependency notification during the busiest
   * moments of a turn, for no reader.
   */
  created() {
    this.timing = createTimingLedger();
  },

  mounted() {
    this.reducedMotion =
      typeof window.matchMedia == "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // NO body scroll lock. The grid behind this is the thing the user is
    // deciding about: they have to be able to scroll to the destination rack
    // while a card is on screen, and cross-puck auto-scroll has to be able to
    // move the page under the dock after an apply.
    this.measureViewport();
    window.addEventListener("resize", this.onViewportChange);
    window.addEventListener("orientationchange", this.onViewportChange);
    // The page's own padding, so the panel sits BESIDE the grid rather than
    // over it. A class on <html> rather than a style on the wrapper, because
    // the wrapper belongs to ManageProducts and this component does not.
    document.documentElement.classList.add("sa-dock-open");
    document.addEventListener("keydown", this.onKeydown);
    // Hold-to-talk and tap open READY, with the SESSION owned by the first
    // press — but not the microphone, which is opened here so the press has
    // only a relay to wait for. Hands-free starts the session on open as the
    // takeover did.
    if (this.holdMode) {
      this.phase = "ready";
      this.prewarmMicrophone();
    } else {
      // The same assignment every other caller of `start()` makes; hands-free
      // is the branch where it is true.
      this.holding = !this.holdMode;
      this.start();
    }
  },

  unmounted() {
    // FIRST, so anything still awaiting inside start() sees it and stops.
    this.teardown = true;
    document.documentElement.classList.remove("sa-dock-open");
    window.removeEventListener("resize", this.onViewportChange);
    window.removeEventListener("orientationchange", this.onViewportChange);
    document.removeEventListener("keydown", this.onKeydown);
    clearInterval(this.usageTimer);
    this.clearConfirmTapGuard();
    this.cancelSpeech();
    if (this.micPermission) this.micPermission.onchange = null;
    this.micPermission = null;
    // Belt and braces: an unmount for any reason must release the microphone —
    // BOTH of the places one can be, the session's and the one opened ahead of
    // the first press. A pre-open that is still in flight sees `closed` on the
    // far side of the permission prompt and releases what it was granted.
    this.session?.stop("unmount");
    this.warmSession?.stop("unmount");
    this.warmSession = null;
  },
};
</script>

<style scoped>
/* No Tailwind here. public/assets/styles.css is a committed, pre-compiled
   artefact that the Vue build does not regenerate, so a utility class not
   already in it silently does nothing. Same reasoning as AppNotifications.vue. */

/* A dock, in two shapes.
   Below AppNotifications (10000) on purpose: a failed write must stay readable
   over the top of this, not be buried by it. Above the nav overlay (9000).
   Nothing here is `inset: 0` any more, nothing dims the page, and the grid
   behind keeps its own scroll — the dock scrolls inside itself. */
.sa-voice {
  position: fixed;
  z-index: 9500;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  color: #0c4a6e;
  font-family: Montserrat, sans-serif;
  text-align: left;
  overscroll-behavior: contain;
}

/* Wide screens. A column down the right-hand edge, hanging from the bottom of
   the search bar that carries the microphone button, so the control that opened
   the dock is never underneath it. */
.sa-voice-panel {
  right: 0;
  bottom: 0;
  border-left: 1px solid #0c4a6e;
  box-shadow: -6px 0 20px rgba(12, 74, 110, 0.1);
}

/* Narrow screens. Anchored to the bottom, two snap heights, and rounded at the
   top so the page is visibly continuing behind it rather than ending. */
.sa-voice-sheet {
  left: 0;
  right: 0;
  bottom: 0;
  border-top: 1px solid #0c4a6e;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -6px 20px rgba(12, 74, 110, 0.14);
  /* The home indicator. Inside the sheet, not on the page, because the sheet is
     the thing sitting over it. */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  transition: height 160ms ease-out;
}

/* Height follows the pointer during a drag, so it must not also be easing
   towards somewhere else — the sheet would arrive a fifth of a second behind
   the thumb and read as lag. */
.sa-voice-sheet-dragging {
  transition: none;
}

/* The handle. 44 px of target around a 36 px pill: the pill is what is seen,
   the band is what is hit. */
.sa-voice-grip {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 12px 0 4px;
  cursor: grab;
  /* The gesture is vertical and ours; the browser must not also read it as a
     page scroll or a pull-to-refresh. */
  touch-action: none;
}
.sa-voice-grip:active {
  cursor: grabbing;
}
.sa-voice-grip-bar {
  width: 36px;
  height: 4px;
  border-radius: 9999px;
  background: #cbd5e1;
}
.sa-voice-grip:focus-visible {
  outline: 2px solid #0c4a6e;
  outline-offset: -2px;
}

.sa-voice-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.5rem 0.5rem 0.875rem;
  border-bottom: 1px solid #e2e8f0;
}

.sa-voice-close {
  flex: none;
  min-width: 52px;
  min-height: 52px;
  border: 0;
  background: transparent;
  color: #0c4a6e;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}
.sa-voice-close:focus-visible {
  outline: 2px solid #0c4a6e;
  outline-offset: -2px;
}

.sa-voice-body {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.875rem;
}

/* The command stage is one SVG coordinate system, so the puck artwork, well
   glyphs, intent rings, arrow and post-apply movement cannot drift apart as the
   phone width changes. Cross-puck previews shrink both real puck canvases into
   the same frame; no destination data is invented when the store lacks it. */
@keyframes sa-voice-delete-pulse {
  to {
    transform: scale(1.08);
    opacity: 0.65;
  }
}

/* Context, not a status: bordered in the same navy as the preview card so it
   reads as part of the surface rather than as another thing that happened.
   Was a text pill; it is now the puck itself, because "KIM48" and a picture of
   KIM48 with five wells filled are different amounts of help to someone
   checking they are talking about the right hardware. */

/* Small on purpose. This is orientation, not a control: nothing here is
   tappable and the wells are never numbered, because at this size a numeral
   would be a smudge. */

/* The dewar has not loaded, so which wells hold boxes is not known. Nothing is
   drawn into the wells at all — twelve painted empty recesses would say the
   puck is empty, which is a claim about someone's samples — and the whole
   drawing steps back to read as a diagram rather than a reading. The caption
   beside it says so in words, which is the part that actually carries it. */

/* The conversation. A single thread runs down both turns so they read as one
   exchange rather than two unrelated status lines, and the turns are told apart
   by weight and colour rather than by chat bubbles — bubbles would be a second
   visual language on a surface that already speaks in cards. */
.sa-voice-talk {
  margin: 0 0 0.875rem;
  padding-left: 0.75rem;
  border-left: 2px solid #bae6fd;
}

.sa-voice-heard {
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #334155;
}
.sa-voice-heard-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}
.sa-voice-heard-text {
  display: block;
  overflow-wrap: anywhere;
}

/* Still being recognised. Lighter and italic, and it settles into the plain
   treatment the moment the completed event lands: a partial can still change
   under the user, and text that might yet be rewritten must not look like text
   a command was built from. */
.sa-voice-heard-live .sa-voice-heard-text {
  font-style: italic;
  color: #64748b;
}

/* The model's turn. Navy, heavier and a size up on the user's line, because
   when there is no card on screen this is the thing to act on. */
.sa-voice-reply {
  margin: 0.625rem 0 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
  color: #0c4a6e;
  animation: sa-voice-settle 160ms ease-out;
}
.sa-voice-reply-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0284c7;
}
.sa-voice-reply-text {
  display: block;
  overflow-wrap: anywhere;
}
.sa-voice-reply-next {
  margin: 0.375rem 0 0;
  font-size: 13px;
  color: #475569;
}

/* Arrival only, and short. The reply appears while the user is looking
   elsewhere on the surface, and a 160 ms settle is what makes them look at it
   without anything continuing to move afterwards. */
@keyframes sa-voice-settle {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* A preview is a form, not an alert (DESIGN.md section 9). */
.sa-voice-card {
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 0.875rem;
  background: #ffffff;
}
.sa-voice-card-destructive {
  border-color: rgb(248 113 113);
  background: #fef2f2;
}

.sa-voice-card-title {
  margin: 0 0 0.5rem;
  font-size: 16px;
  font-weight: 700;
}

.sa-voice-destructive-banner {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.625rem;
  border-left: 4px solid #ef4444;
  background: #ffffff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.sa-voice-rows {
  margin: 0;
  display: grid;
  grid-template-columns: minmax(5.5rem, auto) 1fr;
  gap: 0.375rem 0.75rem;
}
.sa-voice-rows dt {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}
.sa-voice-rows dd {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.sa-voice-nothing-yet {
  margin: 0.75rem 0 0;
  font-size: 13px;
  color: #475569;
}

/* Heavier and darker than "Nothing has changed yet", because it answers a
   question the user is actively worried about — the session went away while
   they were reading a delete card, and the thing they need to know is that
   their inventory is untouched. */
.sa-voice-not-applied {
  margin: 0.75rem 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #0c4a6e;
}

.sa-voice-refusal {
  border: 2px solid #facc15;
  border-radius: 4px;
  background: #fefce8;
  padding: 0.875rem;
}
.sa-voice-refusal-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.sa-voice-refusal-detail {
  margin: 0.125rem 0 0;
  font-size: 14px;
}
.sa-voice-refusal-next {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #475569;
}

.sa-voice-applied {
  border: 2px solid #22c55e;
  border-radius: 4px;
  padding: 0.875rem;
}
.sa-voice-applied-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.sa-voice-applied-detail {
  margin: 0.125rem 0 0;
  font-size: 14px;
}

.sa-voice-hint-lead {
  margin: 0 0 0.5rem;
  font-size: 16px;
  font-weight: 600;
}
.sa-voice-hint-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.sa-voice-hint-list li {
  margin-bottom: 0.5rem;
  font-size: 15px;
  line-height: 1.45;
  color: #334155;
}
.sa-voice-hint-foot {
  margin: 0.75rem 0 0;
  font-size: 13px;
  color: #475569;
}

/* Thumb zone. Padded for the home indicator so the last button is never half
   under it on a phone. */
.sa-voice-foot {
  flex: none;
  padding: 0.625rem 0.875rem calc(0.875rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

/* Signal-driven level meter — DESIGN.md section 10.
   Height is the readout, colour only corroborates it, and there is deliberately
   NO transition: bound 1 of the amendment requires the motion to stop in the
   same frame the signal does, and an eased height would keep moving after the
   microphone had gone quiet. Flicker is handled by peak-decay smoothing in the
   component, not by CSS easing. */
.sa-voice-meter {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 22px;
  margin-bottom: 0.25rem;
}
.sa-voice-meter-bar {
  flex: 1 1 0;
  /* Resting height. A meter reading zero is still a meter, not an empty box. */
  height: 25%;
  border-radius: 2px;
  background: #e2e8f0;
}
/* A connected meter reading zero and a dead meter are different things, and the
   bars alone cannot tell them apart at a glance. The track darkens while the
   microphone is open — a state, not an animation, so it says nothing about
   signal that the bar heights are not already saying. */
.sa-voice-meter-live .sa-voice-meter-bar {
  background: #cbd5e1;
}
.sa-voice-meter-live .sa-voice-meter-bar-on {
  height: 100%;
  background: #0c4a6e;
}

.sa-voice-meter-caption {
  margin: 0 0 0.125rem;
  font-size: 12px;
  color: #475569;
}
.sa-voice-usage {
  margin: 0;
  font-size: 12px;
  color: #475569;
}

/* A microphone the browser refused. The button keeps its place and its size —
   the panel has not changed shape, the device has changed answer — and reads
   as unavailable. `:deep` because the button is its own component and the one
   thing it does not own is whether the browser will grant a microphone; the
   press itself is already inert in `onHoldStart`, so this is the part the eye
   needs rather than the part the finger does. */
.sa-voice-mic-blocked :deep(.sa-voice-hold),
.sa-voice-mic-blocked :deep(.sa-voice-hold-label) {
  opacity: 0.45;
}
.sa-voice-mic-blocked :deep(.sa-voice-hold) {
  cursor: not-allowed;
}

/* Tabular figures: three numbers side by side that shift their own columns as
   they update are harder to read than three that do not. */
.sa-voice-latency {
  margin: 0.125rem 0 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: #475569;
}

/* One flex container for the pair. `flex-direction` is set inline from
   `footerDirection`, so the DOM order can stay "act, then leave" on every card
   while the painted order moves for the one case that needs it. */
.sa-voice-actions {
  display: flex;
  gap: 0.625rem;
  margin-top: 0.625rem;
}

/* 52 px: the preferred gloved-hand target, not the 44 px floor. */
.sa-voice-btn {
  display: block;
  width: 100%;
  min-height: 52px;
  padding: 0.75rem 1rem;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
}
.sa-voice-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.sa-voice-btn:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}

.sa-voice-btn-primary {
  background: #0c4a6e;
  color: #ffffff;
}
.sa-voice-btn-danger {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
}
.sa-voice-btn-quiet {
  background: #ffffff;
  color: #0c4a6e;
}

/* A tablet-width sheet spans the whole screen, and a line of body text 900 px
   wide is a line nobody finishes. Capped below 1024 only: above that the dock
   is a 380 px panel and a 34 rem cap inside it would do nothing but crop. */
@media (min-width: 640px) and (max-width: 1023.98px) {
  .sa-voice-sheet .sa-voice-body,
  .sa-voice-sheet .sa-voice-foot {
    max-width: 34rem;
    margin: 0 auto;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  /* The reply still appears; it just stops sliding to do it. */
  .sa-voice-reply {
    animation: none;
  }
  .sa-voice-sheet {
    transition: none;
  }
}
</style>

<!--
  Unscoped, and the only unscoped rule this component ships.

  The panel has to push the page rather than cover it — a dock that hides the
  right-hand column of the puck grid is the takeover again, in a narrower
  window. The push therefore has to land on an element this component does not
  own, so it is expressed as a class on <html> that the dock adds while it is
  mounted and removes when it goes.

  `.sa-dock-push` is the intended target: a wrapper inside Manage Products.
  Applying it means editing `ManageProducts.vue`, which this packet may not do,
  so `#app` is the fallback and the one actually doing the work today. It is the
  Vue mount point, so it holds the whole page including the sticky top row and
  search bar, and padding on it moves all of them left of the panel together.
  Both selectors ship: whichever wrapper appears first wins, and neither does
  anything once the dock unmounts.

  Guarded at 1024 px, the same line `layoutFor` draws, because below it the dock
  is a bottom sheet and a page pushed 380 px to the left would be off screen.
-->
<style>
@media (min-width: 1024px) {
  html.sa-dock-open .sa-dock-push,
  html.sa-dock-open #app {
    padding-right: 380px;
  }
}
</style>
